import { useState, useRef, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { PageHeader } from "@/components/layout/PageHeader";
import { StatusPill } from "@/components/shared/StatusPill";
import { AICard, AIBadge } from "@/components/shared/AICard";
import { CommunicationTimeline } from "@/components/shared/CommunicationTimeline";
import { DocumentPanel } from "@/components/shared/DocumentPanel";
import { AutoFileDialog } from "@/components/shared/AutoFileDialog";
import { Loading } from "@/components/shared/Loading";
import { EmptyState } from "@/components/shared/EmptyState";
import { HandoffConfirmDialog } from "@/components/shared/HandoffConfirmDialog";
import { useFileSelectConfirm, FileSelectConfirmBar } from "@/components/shared/FileSelectConfirm";
import { displayLabel } from "@/components/shared/StatusPill";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  useCommercialWorkspace, useCommunications, useCustomerDocuments, useInquiryDocuments, useVendors, useMe,
  useRateRequestDispatchReadiness, useCustomer, useInquiryTradeDocuments,
} from "@/api/hooks";
import { api } from "@/api/client";
import { money } from "@/lib/format";
import { useApp } from "@/context/AppContext";
import { apiErrorMessage, isConflict } from "@/lib/apiError";
import {
  ArrowLeft, Mail, MessageCircle, Phone, FileText, UploadCloud, Sparkles, ChevronRight,
  Check, ShieldAlert, ArrowRight, AlertTriangle, Lock, XCircle, RotateCcw, PlusCircle,
  Loader2, Eye, Send,
} from "lucide-react";

// ==========================================================================
// Phase 4.7 — Close Opportunity. Plain-language outcome options; the backend
// stores structured reasonCode/reasonText (see
// backend/services/commercial.py: close_inquiry_lost / defer_inquiry).
// "Customer postponed" deliberately routes to the distinct ON_HOLD_CUSTOMER
// outcome (POST /commercial/inquiries/{id}/defer), not Lost — see
// docs/PHASE_4_7_COMMERCIAL_CLOSURE.md.
// ==========================================================================
const CLOSE_OUTCOME_OPTIONS = [
  { key: "competitor", label: "Lost to a competitor", reasonCode: "COMPETITOR", askCompetitor: true },
  { key: "price", label: "Price too high", reasonCode: "PRICE" },
  { key: "not_interested", label: "Not interested anymore", reasonCode: "OTHER" },
  { key: "cancelled", label: "Customer cancelled the shipment", reasonCode: "CUSTOMER_CANCELLED" },
  { key: "credit_terms", label: "Customer rejected payment/credit terms", reasonCode: "CREDIT_PAYMENT_TERMS" },
  { key: "transit", label: "Transit time didn't work for the customer", reasonCode: "TRANSIT" },
  { key: "service", label: "We can't service this requirement", reasonCode: "SERVICE_NOT_AVAILABLE" },
  { key: "duplicate", label: "Duplicate enquiry", reasonCode: "DUPLICATE" },
  { key: "no_response", label: "No response after repeated attempts", reasonCode: "NO_RESPONSE" },
  { key: "other", label: "Other", reasonCode: "OTHER" },
];

export function CloseOpportunityDialog({ open, onOpenChange, inquiry, client, onClosed }) {
  const [step, setStep] = useState("choose"); // choose | confirm
  const [outcome, setOutcome] = useState("close"); // "close" | "postpone"
  const [reasonKey, setReasonKey] = useState(null);
  const [note, setNote] = useState("");
  const [competitorName, setCompetitorName] = useState("");
  const [reactivationDate, setReactivationDate] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setStep("choose"); setOutcome("close"); setReasonKey(null); setNote(""); setCompetitorName(""); setReactivationDate(""); };
  const close = (v) => { if (!v) reset(); onOpenChange(v); };

  const selected = CLOSE_OUTCOME_OPTIONS.find((o) => o.key === reasonKey);
  const canContinue = outcome === "postpone" ? true : !!selected;
  const canSubmit = outcome === "postpone" ? !!reactivationDate : true;

  const submit = async () => {
    setBusy(true);
    try {
      if (outcome === "postpone") {
        await api.post(`/commercial/inquiries/${inquiry.id}/defer`, {
          expectedVersion: inquiry.version, reasonText: note || undefined, reactivationDate,
        });
        toast.success("Opportunity paused — a follow-up is scheduled.");
      } else {
        await api.post(`/commercial/inquiries/${inquiry.id}/close`, {
          expectedVersion: inquiry.version, reasonCode: selected.reasonCode, reasonText: note || undefined,
          competitorName: selected.askCompetitor && competitorName ? competitorName : undefined,
        });
        toast.success("Opportunity closed.");
      }
      close(false);
      onClosed?.();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not close this opportunity."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-lg" data-testid="close-opportunity-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Close opportunity</DialogTitle>
        </DialogHeader>

        <div className="text-[12px] text-slate-500 -mt-2 grid grid-cols-2 gap-2 border-b border-slate-100 pb-3">
          <div><span className="text-slate-400">Customer</span><div className="font-medium text-slate-700">{client?.name || inquiry?.clientId}</div></div>
          <div><span className="text-slate-400">Inquiry</span><div className="font-medium text-slate-700">{inquiry?.id} · {displayLabel(inquiry?.status)}</div></div>
        </div>

        {step === "choose" && (
          <div className="space-y-3">
            <div className="flex gap-2">
              <Button size="sm" variant={outcome === "close" ? "default" : "outline"} className="flex-1 h-9" onClick={() => setOutcome("close")} data-testid="outcome-close-btn">Not interested / Lost</Button>
              <Button size="sm" variant={outcome === "postpone" ? "default" : "outline"} className="flex-1 h-9" onClick={() => setOutcome("postpone")} data-testid="outcome-postpone-btn">Customer postponed</Button>
            </div>

            {outcome === "close" && (
              <div className="space-y-1.5" data-testid="close-reason-list">
                <Label className="text-[11px] uppercase tracking-wider text-slate-400">Reason</Label>
                <div className="grid grid-cols-2 gap-1.5">
                  {CLOSE_OUTCOME_OPTIONS.map((o) => (
                    <button key={o.key} type="button" onClick={() => setReasonKey(o.key)} data-testid={`close-reason-${o.key}`}
                      className={`text-left text-[12.5px] rounded-lg border px-2.5 py-2 transition-colors ${reasonKey === o.key ? "border-indigo-400 bg-indigo-50 text-indigo-800" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                      {o.label}
                    </button>
                  ))}
                </div>
                {selected?.askCompetitor && (
                  <div className="pt-1">
                    <Label className="text-[11px] text-slate-400">Competitor name (optional)</Label>
                    <Input value={competitorName} onChange={(e) => setCompetitorName(e.target.value)} placeholder="e.g. XYZ Logistics" data-testid="competitor-name-input" />
                  </div>
                )}
                {reasonKey === "no_response" && (
                  <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                    No Response can only be used once enough contact attempts have been logged over the required
                    window — one missed call is not enough. If this is rejected, log another contact attempt first.
                  </div>
                )}
              </div>
            )}

            {outcome === "postpone" && (
              <div className="space-y-2">
                <Label className="text-[11px] text-slate-400">Next follow-up / reactivation date</Label>
                <Input type="date" value={reactivationDate} onChange={(e) => setReactivationDate(e.target.value)} data-testid="reactivation-date-input" />
              </div>
            )}

            <div>
              <Label className="text-[11px] text-slate-400">Note (optional)</Label>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Anything useful for later (customer feedback, context)…" data-testid="close-note-input" />
            </div>

            <DialogFooter>
              <Button size="sm" variant="outline" onClick={() => close(false)}>Cancel</Button>
              <Button size="sm" disabled={!canContinue} onClick={() => setStep("confirm")} data-testid="close-continue-btn">Continue</Button>
            </DialogFooter>
          </div>
        )}

        {step === "confirm" && (
          <div className="space-y-3">
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-[13px] text-amber-800" data-testid="close-impact-copy">
              {outcome === "postpone"
                ? "This will pause the active Sales opportunity and stop current follow-up tasks until the reactivation date. Customer history and documents will remain available."
                : "This will close the active Sales opportunity and stop current Sales follow-up tasks. Customer history and documents will remain available. Nothing is deleted."}
            </div>
            <div className="text-sm text-slate-600">
              {outcome === "postpone"
                ? <>Outcome: <b>Customer postponed</b> · Follow up on <b>{reactivationDate}</b></>
                : <>Outcome: <b>{selected?.label}</b></>}
            </div>
            <DialogFooter>
              <Button size="sm" variant="outline" onClick={() => setStep("choose")}>Back</Button>
              <Button size="sm" disabled={busy || !canSubmit} onClick={submit} data-testid="close-confirm-btn"
                className={outcome === "postpone" ? "" : "bg-red-600 hover:bg-red-700"}>
                {busy ? "Working…" : outcome === "postpone" ? "Pause opportunity" : "Close opportunity"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---- Post-closure / paused panel ----
export function ClosedOpportunityPanel({ inquiry, onResume, onReactivate, busy }) {
  if (inquiry.status === "LOST") {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="closed-opportunity-panel">
        <div className="flex items-center gap-2 text-slate-700 font-semibold text-sm"><XCircle className="h-4 w-4 text-slate-400" />Opportunity Closed</div>
        <div className="text-[13px] text-slate-500 mt-1">
          Reason: {inquiry.lostReasonCode || "Not specified"}{inquiry.competitorName ? ` · ${inquiry.competitorName}` : ""}
          <br />Closed: {(inquiry.lostAt || "").slice(0, 10)} · {inquiry.lostBy || ""}
        </div>
        {inquiry.lostReasonText && <div className="text-[12px] text-slate-500 mt-1 italic">"{inquiry.lostReasonText}"</div>}
        <div className="flex items-center gap-2 mt-3">
          <Button size="sm" variant="outline" className="h-8" onClick={onReactivate} disabled={busy} data-testid="reactivate-btn">
            <PlusCircle className="h-3.5 w-3.5 mr-1.5" />Create new inquiry
          </Button>
        </div>
      </div>
    );
  }
  if (inquiry.status === "ON_HOLD_CUSTOMER") {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="closed-opportunity-panel">
        <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm"><RotateCcw className="h-4 w-4" />Opportunity Paused</div>
        <div className="text-[13px] text-amber-700 mt-1">
          Customer postponed · Follow up on {(inquiry.reactivationDate || "").slice(0, 10) || "—"}
        </div>
        {inquiry.deferredReason && <div className="text-[12px] text-amber-700 mt-1 italic">"{inquiry.deferredReason}"</div>}
        <Button size="sm" className="h-8 mt-3 bg-amber-600 hover:bg-amber-700" onClick={onResume} disabled={busy} data-testid="resume-btn">
          Resume now
        </Button>
      </div>
    );
  }
  return null;
}

// Item 3 (Aug 2026 UX fix) — once an inquiry is WON, Sales' own work on it
// is done; the quotation/rate/feasibility/acceptance detail below is now
// downstream history, not a live to-do. This replaces all of that with one
// simple status summary — what was accepted, and where the order stands
// now (Finance/Operations) — pointing back at Customer Profile (top status
// badge, Previous Trades once delivered) instead of repeating live
// operational detail that isn't Sales' to act on.
export function WonOrderStatusPanel({ acceptance, currentVersion, financeSummary }) {
  return (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="won-order-status-panel">
      <div className="flex items-center gap-2 text-emerald-800 font-semibold text-sm"><Check className="h-4 w-4" />Order Accepted</div>
      <div className="text-[13px] text-emerald-700 mt-1">
        {currentVersion ? `${money(currentVersion.total, currentVersion.currency)} · ${currentVersion.paymentTerms || "—"}` : "Quotation accepted."}
      </div>
      <div className="text-[13px] text-emerald-700 mt-1">
        Status: {financeSummary?.financeStatus || (acceptance?.status === "VERIFIED" ? "Sent to Finance" : "With Finance")}
      </div>
      <div className="text-[12px] text-emerald-600 mt-2">
        This order has moved past Sales into Finance/Operations. Check the customer's current status at the top of the Customer Profile, or under Previous Trades once delivered.
      </div>
    </div>
  );
}

// ==========================================================================
// Phase 4.6 — unified Sales commercial workspace. One screen per customer
// commercial situation (Lead or Inquiry), driven entirely by the real
// Phase 3 backend via GET /commercial/inquiries/{id}/workspace (server-
// computed nextAction/blockers — no client-side workflow re-implementation)
// plus the existing customer communications/documents endpoints. Replaces
// the Phase 3 "functional-proof" Commercial.jsx as the day-to-day surface.
// ==========================================================================

function Card({ title, right, children, testId, tone = "default" }) {
  const toneCls = tone === "warn" ? "border-amber-200 bg-amber-50/40" : tone === "danger" ? "border-red-200 bg-red-50/30" : "border-slate-200 bg-white";
  return (
    <div className={`rounded-xl border ${toneCls} p-4`} data-testid={testId}>
      {(title || right) && (
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          {title && <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{title}</div>}
          {right}
        </div>
      )}
      {children}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
      <div className="text-sm font-medium text-slate-800">{value ?? <span className="text-slate-300">—</span>}</div>
    </div>
  );
}

// ---- Next action ----
export function NextActionPanel({ nextAction }) {
  if (!nextAction) return null;
  const isDone = nextAction.actionType === "none" || nextAction.actionType === "with_finance";
  return (
    <div
      className={`rounded-xl border p-4 ${isDone ? "border-emerald-200 bg-emerald-50" : "border-indigo-200 bg-indigo-50"}`}
      data-testid="next-action-panel"
    >
      <div className={`flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider ${isDone ? "text-emerald-600" : "text-indigo-500"}`}>
        What to do next
        {nextAction.aiGenerated && <AIBadge>AI</AIBadge>}
      </div>
      <div className={`text-base font-display font-semibold mt-0.5 ${isDone ? "text-emerald-900" : "text-indigo-900"}`}>{nextAction.what}</div>
      {nextAction.why && <div className={`text-[13px] mt-1 ${isDone ? "text-emerald-700" : "text-indigo-700"}`}>{nextAction.why}</div>}
      {nextAction.aiGenerated === false && (
        <div className="text-[11px] mt-1.5 text-slate-400 italic">AI recommendation unavailable — showing the current required action</div>
      )}
      {nextAction.whatHappensNext && (
        <div className="text-[12px] mt-1.5 text-slate-500 flex items-center gap-1.5"><ArrowRight className="h-3 w-3 shrink-0" />{nextAction.whatHappensNext}</div>
      )}
    </div>
  );
}

// ---- Requirement summary (filtered to what's relevant for this mode) ----
const REQUIREMENT_ROWS = [
  ["mode", "Mode"], ["direction", "Direction"], ["scope", "Service scope"], ["origin", "Origin"],
  ["destination", "Destination"], ["pickup", "Pickup"], ["delivery", "Delivery"], ["commodity", "Commodity"],
  ["pieces", "Pieces"], ["weight", "Weight (kg)"], ["dimensions", "Dimensions"], ["cbm", "CBM"],
  ["equipment", "Equipment"], ["packageType", "Package type"], ["incoterm", "Incoterm"],
  ["cargoReadyDate", "Cargo ready date"], ["requestedDeliveryDate", "Requested delivery"],
  ["shipper", "Shipper"], ["consignee", "Consignee"],
];
const RISK_ROWS = [
  ["dg", "Dangerous goods", (i) => (i.dg ? `Yes${i.dgClass ? ` · Class ${i.dgClass}` : ""}` : null)],
  ["battery", "Battery", (i) => (i.battery ? (i.batteryDeclaration || "Flagged — declaration pending") : null)],
  ["temperatureControlled", "Temperature control", (i) => (i.temperatureControlled ? (i.temperatureRange || "Flagged — range pending") : null)],
  ["restricted", "Restricted/permit cargo", (i) => (i.restricted ? (i.permitInfo || "Flagged — permit info pending") : null)],
  ["deliveryAppointmentRequired", "Delivery appointment", (i) => (i.deliveryAppointmentRequired ? (i.deliveryAppointment || "Required — not yet scheduled") : null)],
];

const REQUIREMENT_EDIT_KEYS = [
  "mode", "direction", "scope", "serviceType", "origin", "destination", "pickup", "delivery",
  "commodity", "description", "pieces", "packageType", "weight", "dimensions", "cbm", "equipment",
  "incoterm", "cargoReadyDate", "requestedDeliveryDate", "shipper", "consignee", "billingParty",
  "dg", "dgClass", "unNumber", "battery", "batteryDeclaration", "temperatureControlled",
  "temperatureRange", "perishable", "perishableHandling", "restricted", "permitInfo",
  "insuranceRequired", "insuranceValue", "deliveryAppointmentRequired", "deliveryAppointment",
];
const REQUIREMENT_FLAG_KEYS = new Set([
  "dg", "battery", "temperatureControlled", "perishable", "restricted", "insuranceRequired",
  "deliveryAppointmentRequired",
]);
const REQUIREMENT_NUMBER_KEYS = new Set(["pieces", "weight", "cbm", "insuranceValue"]);

function requirementDraft(inquiry) {
  return Object.fromEntries(REQUIREMENT_EDIT_KEYS.map((key) => [
    key,
    inquiry?.[key] ?? (REQUIREMENT_FLAG_KEYS.has(key) ? false : ""),
  ]));
}

function normalizeRequirementFields(value) {
  return Object.fromEntries(REQUIREMENT_EDIT_KEYS.map((key) => {
    const raw = value[key];
    if (REQUIREMENT_FLAG_KEYS.has(key)) return [key, Boolean(raw)];
    const cleaned = typeof raw === "string" ? raw.trim() : raw;
    if (REQUIREMENT_NUMBER_KEYS.has(key) && cleaned !== "" && cleaned !== null && cleaned !== undefined) {
      const numeric = Number(cleaned);
      return [key, Number.isFinite(numeric) ? numeric : cleaned];
    }
    return [key, cleaned ?? ""];
  }));
}

function RequirementEditorDialog({ open, onOpenChange, value, onChange, onSubmit, busy }) {
  if (!value) return null;
  const set = (key) => (event) => onChange({ ...value, [key]: event.target.value });
  const toggle = (key) => (event) => onChange({ ...value, [key]: event.target.checked });
  const input = (key, label, type = "text") => (
    <div>
      <Label htmlFor={`requirement-${key}`}>{label}</Label>
      <Input id={`requirement-${key}`} type={type} value={value[key] ?? ""} onChange={set(key)} className="mt-1" data-testid={`requirement-${key}`} />
    </div>
  );
  const flag = (key, label) => (
    <label className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
      <input type="checkbox" checked={Boolean(value[key])} onChange={toggle(key)} data-testid={`requirement-${key}`} className="h-4 w-4" />
      {label}
    </label>
  );

  return (
    <Dialog open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-3xl" data-testid="requirements-dialog">
        <DialogHeader>
          <DialogTitle>Edit / complete shipment requirements</DialogTitle>
          <DialogDescription>Complete the operational facts received from the customer. Saving recalculates blockers and updates the work queue.</DialogDescription>
        </DialogHeader>
        <form onSubmit={(event) => { event.preventDefault(); onSubmit(); }} className="space-y-5">
          <section>
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Service and route</div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <div><Label htmlFor="requirement-mode">Mode</Label><select id="requirement-mode" value={value.mode} onChange={set("mode")} data-testid="requirement-mode" className="mt-1 h-9 w-full rounded-md border border-input bg-white px-3 text-sm"><option value="">Select mode</option>{["Air", "FCL", "LCL", "Courier", "Land"].map((item) => <option key={item}>{item}</option>)}</select></div>
              <div><Label htmlFor="requirement-direction">Direction</Label><select id="requirement-direction" value={value.direction} onChange={set("direction")} data-testid="requirement-direction" className="mt-1 h-9 w-full rounded-md border border-input bg-white px-3 text-sm"><option value="">Select direction</option>{["Import", "Export", "CrossBorder"].map((item) => <option key={item}>{item}</option>)}</select></div>
              {input("scope", "Service scope")}
              {input("serviceType", "Service type")}
              {input("origin", "Origin")}
              {input("destination", "Destination")}
              {input("pickup", "Pickup location")}
              {input("delivery", "Delivery location")}
            </div>
          </section>
          <section>
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Cargo</div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {input("commodity", "Commodity")}
              {input("description", "Cargo description")}
              {input("pieces", "Pieces", "number")}
              {input("packageType", "Package type")}
              {input("weight", "Weight (kg)", "number")}
              {input("dimensions", "Dimensions")}
              {input("cbm", "CBM", "number")}
              {input("equipment", "Equipment / container")}
              {input("incoterm", "Incoterm")}
              {input("cargoReadyDate", "Cargo ready date", "date")}
              {input("requestedDeliveryDate", "Requested delivery date", "date")}
              {input("shipper", "Shipper")}
              {input("consignee", "Consignee")}
              {input("billingParty", "Billing party")}
            </div>
          </section>
          <section>
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Special handling and risk</div>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {flag("dg", "Dangerous goods")}{flag("battery", "Contains batteries")}{flag("temperatureControlled", "Temperature controlled")}
              {flag("perishable", "Perishable")}{flag("restricted", "Restricted / permit cargo")}{flag("insuranceRequired", "Insurance required")}
              {flag("deliveryAppointmentRequired", "Delivery appointment required")}
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {value.dg && <>{input("dgClass", "DG class")}{input("unNumber", "UN number")}</>}
              {value.battery && input("batteryDeclaration", "Battery declaration")}
              {value.temperatureControlled && input("temperatureRange", "Temperature range")}
              {value.perishable && input("perishableHandling", "Perishable handling")}
              {value.restricted && input("permitInfo", "Permit information")}
              {value.insuranceRequired && input("insuranceValue", "Insurance value", "number")}
              {value.deliveryAppointmentRequired && input("deliveryAppointment", "Delivery appointment")}
            </div>
          </section>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="requirements-save">{busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}Save requirements</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function RequirementSummary({ inquiry, completeness, onEdit }) {
  const required = new Set(completeness?.requiredFields || []);
  const rows = REQUIREMENT_ROWS.filter(([k]) => required.has(k) || inquiry[k]);
  const riskRows = RISK_ROWS.map(([k, label, fmt]) => [k, label, fmt(inquiry)]).filter(([, , v]) => v);
  return (
    <Card title="Requirement summary" testId="requirement-summary" right={onEdit && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onEdit} data-testid="edit-requirements-btn">Edit / complete requirements</Button>}>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-3">
        {rows.map(([k, label]) => <Field key={k} label={label} value={inquiry[k]} />)}
      </div>
      {riskRows.length > 0 && (
        <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-3">
          {riskRows.map(([k, label, v]) => <Field key={k} label={label} value={v} />)}
        </div>
      )}
    </Card>
  );
}

// ---- Missing information ----
// Item 3 (round 2) India KYC docs are also selectable here, alongside
// whatever document-type blockers the backend already reports — the same 3
// names surfaced in the onboarding flow and the client document checklist.
const EXTRA_DOCUMENT_ITEMS = [
  { field: "kyc_aadhaar", label: "Aadhaar Card" },
  { field: "kyc_pan", label: "PAN Card" },
  { field: "kyc_cin", label: "CIN Certificate" },
];

// Item 5 (Aug 2026 Sales dashboard scope pass, round 2) — previously every
// "request_document" blocker opened its own Composer session (one send per
// item). This lets Sales tick any combination of missing documents —
// existing blockers, the 3 KYC documents above, and a free-text "Other" —
// and send exactly ONE combined request. Reuses the existing Composer
// (onSendDocumentRequest opens it once, with every selected item folded
// into a single intent) — no new send mechanism, no persisted "document
// request" entity (consistent with how askCustomer already worked before
// this change).
// Aug 2026 item 3 — generalized so the exact same multi-select "ask/upload/
// send one combined request" UI can be reused for the new Trade Documents
// section (a second instance, its own item set, its own Card) instead of
// building a second component. `extraItems` defaults to the original KYC
// doc list to keep every existing call site's behavior unchanged;
// `includeExtraInCount`/`blockingLabel`/`cardTitle`/`emptyLabel`/`testId`
// let a second instance (e.g. Trade Documents) present itself distinctly.
export function MissingRequirementList({ blockers, onAskCustomer, onSendDocumentRequest, customerId, inquiryId, documents, onPreview,
  extraItems = EXTRA_DOCUMENT_ITEMS, includeExtraInCount = false, blockingLabel = "Blocking",
  cardTitle, emptyLabel = "All required information is complete.", testId = "missing-requirements" }) {
  const [selected, setSelected] = useState([]);
  const [otherChecked, setOtherChecked] = useState(false);
  const [otherText, setOtherText] = useState("");
  const [uploadState, setUploadState] = useState({}); // field -> "uploading" | "uploaded"
  // Item 1 — a chosen file is staged per blocker field here (never
  // uploaded) until Submit is clicked in that row's confirm bar.
  const [pendingFiles, setPendingFiles] = useState({});
  const fileRefs = useRef({});
  const qc = useQueryClient();

  const infoBlockers = (blockers || []).filter((b) => b.actionType !== "request_document");
  const docBlockers = (blockers || []).filter((b) => b.actionType === "request_document");
  // Every missing item — info fields (Origin/Destination/Commodity/...) and
  // document-type blockers alike — can be requested from the customer or
  // supported by an uploaded file. KYC docs are always offered here too.
  const items = [
    ...infoBlockers,
    ...docBlockers,
    ...extraItems,
  ];

  // Real submitted/pending status, matched against actually uploaded
  // documents (same loose type-substring match the backend's document
  // checklist uses) rather than only the transient in-session upload state.
  const matchDoc = (label) => {
    const key = label.toLowerCase();
    return (documents || []).find((d) => {
      const t = (d.type || "").toLowerCase();
      return t === key || t.includes(key) || key.includes(t);
    }) || null;
  };

  const toggle = (field) => setSelected((cur) => (cur.includes(field) ? cur.filter((f) => f !== field) : [...cur, field]));

  const send = () => {
    const labels = items.filter((b) => selected.includes(b.field)).map((b) => b.label);
    if (otherChecked && otherText.trim()) labels.push(otherText.trim());
    if (!labels.length) return;
    onSendDocumentRequest(labels);
    setSelected([]); setOtherChecked(false); setOtherText("");
  };

  const triggerUpload = (field) => fileRefs.current[field]?.click();

  // Selecting a file only stages it (never uploads) — Submit in the
  // per-row confirm bar performs the actual upload. Re-Upload reopens the
  // picker and replaces the staged file; Cancel discards it untouched.
  const stageFor = (b, e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) setPendingFiles((s) => ({ ...s, [b.field]: file }));
  };
  const cancelFor = (b) => setPendingFiles((s) => { const n = { ...s }; delete n[b.field]; return n; });
  const reUploadFor = (b) => triggerUpload(b.field);

  const submitFor = async (b) => {
    const file = pendingFiles[b.field];
    if (!file) return;
    setUploadState((s) => ({ ...s, [b.field]: "uploading" }));
    const fd = new FormData();
    fd.append("file", file);
    fd.append("jobId", "auto");
    fd.append("docType", b.label);
    fd.append("category", b.uploadCategory || "GENERAL");
    if (customerId) fd.append("customerId", customerId);
    if (inquiryId) fd.append("inquiryId", inquiryId);
    try {
      await api.post("/documents/upload", fd);
      setUploadState((s) => ({ ...s, [b.field]: "uploaded" }));
      setPendingFiles((s) => { const n = { ...s }; delete n[b.field]; return n; });
      qc.invalidateQueries({ queryKey: ["cust-docs", customerId] });
      qc.invalidateQueries({ queryKey: ["inquiry-docs", inquiryId] });
      qc.invalidateQueries({ queryKey: ["inquiry-trade-docs", inquiryId] });
      toast.success(`${b.label} uploaded — pending review`);
    } catch (err) {
      setUploadState((s) => { const n = { ...s }; delete n[b.field]; return n; });
      toast.error(apiErrorMessage(err, "Upload failed"));
    }
  };

  const outstandingExtra = extraItems.filter((b) => !matchDoc(b.label));
  const totalCount = infoBlockers.length + docBlockers.length + (includeExtraInCount ? outstandingExtra.length : 0);
  const nothingMissing = totalCount === 0;
  const blocking = infoBlockers.filter((b) => b.blocking);
  const selectedCount = selected.length + (otherChecked && otherText.trim() ? 1 : 0);
  return (
    <Card
      title={cardTitle ?? (nothingMissing ? undefined : `${totalCount} item${totalCount !== 1 ? "s" : ""} still needed`)}
      tone={blocking.length ? "warn" : "default"}
      testId={testId}
    >
      {nothingMissing && (
        <div className="flex items-center gap-2 text-sm text-emerald-700 font-medium mb-3"><Check className="h-4 w-4" />{emptyLabel}</div>
      )}
      <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Select one or more items — ask, upload, or send as one request</div>
      <div className="space-y-1.5" data-testid="doc-request-select">
            {items.map((b) => {
              const state = uploadState[b.field];
              const matched = matchDoc(b.label);
              const submitted = state === "uploaded" || !!matched;
              const pendingFile = pendingFiles[b.field];
              return (
                <div key={b.field} data-testid={`blocker-${b.field}`} className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                  <div className="flex flex-wrap items-center gap-x-2.5 gap-y-2">
                    <input type="checkbox" className="h-4 w-4 shrink-0" checked={selected.includes(b.field)} onChange={() => toggle(b.field)} data-testid={`docsel-${b.field}`} />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-slate-800 flex items-center gap-1.5">
                        {b.label}
                        {b.blocking && <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 rounded px-1.5 py-0.5">{blockingLabel}</span>}
                        {b.optional && <span className="text-[10px] font-semibold text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">Optional</span>}
                      </div>
                      {b.prompt && <div className="text-[12px] text-slate-500">{b.prompt}</div>}
                      {matched && (matched.date || matched.review) && (
                        <div className="text-[11px] text-slate-400">Uploaded {matched.date || "—"} · {matched.review || "Pending review"}</div>
                      )}
                    </div>
                    <div className="ml-auto flex min-w-0 flex-wrap items-center justify-end gap-1.5">
                      <span className={`text-[10px] font-semibold rounded px-1.5 py-0.5 ${submitted ? "text-emerald-700 bg-emerald-100" : "text-slate-500 bg-slate-100"}`} data-testid={`docstatus-${b.field}`}>
                        {submitted ? "Submitted" : "Pending"}
                      </span>
                      {onAskCustomer && b.actionLabel && (
                        <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`blocker-action-${b.field}`} onClick={() => onAskCustomer(b)}>
                          {b.actionLabel}
                        </Button>
                      )}
                      <input ref={(el) => (fileRefs.current[b.field] = el)} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden"
                        onChange={(e) => stageFor(b, e)} data-testid={`upload-input-${b.field}`} />
                      {!pendingFile && (
                        <Button size="sm" variant="outline" className="h-7 text-xs" disabled={state === "uploading"}
                          onClick={() => triggerUpload(b.field)} data-testid={`upload-btn-${b.field}`}>
                          {state === "uploading" ? "Uploading…" : "Upload"}
                        </Button>
                      )}
                      {matched && onPreview && (
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onPreview(matched)} data-testid={`preview-btn-${b.field}`}>
                          Preview
                        </Button>
                      )}
                    </div>
                  </div>
                  {pendingFile && (
                    <FileSelectConfirmBar pending={pendingFile} busy={state === "uploading"} onSubmit={() => submitFor(b)} onCancel={() => cancelFor(b)} onReUpload={() => reUploadFor(b)} className="mt-2" />
                  )}
                </div>
              );
            })}
            <label data-testid="docsel-other" className="flex flex-wrap items-center gap-2.5 rounded-lg border border-slate-200 bg-white px-3 py-2 cursor-pointer">
              <input type="checkbox" className="h-4 w-4" checked={otherChecked} onChange={() => setOtherChecked((v) => !v)} />
              <span className="text-sm font-medium text-slate-800 shrink-0">Other Required Document</span>
              {otherChecked && (
                <Input value={otherText} onChange={(e) => setOtherText(e.target.value)} placeholder="Document name"
                  className="h-7 min-w-[12rem] flex-1 text-xs" data-testid="docsel-other-text" />
              )}
            </label>
      </div>
      <div className="mt-2.5 flex justify-end">
        <Button size="sm" type="button" className="h-7 text-xs" disabled={!selectedCount} onClick={send} data-testid="send-document-request-btn">
          Send Document Request{selectedCount > 1 ? ` (${selectedCount})` : ""}
        </Button>
      </div>
    </Card>
  );
}

// ---- Trade Documents (item 3) ----
// Mandatory Trade Document checklist for this specific Lead/Trade/Enquiry
// (Inquiry) — CLEARLY SEPARATE from the KYC document checklist (a
// customer-level, not inquiry-level, concept shown on Customer360's own KYC
// review section). Data comes from GET /commercial/inquiries/{iid}/trade-
// documents: a fixed mandatory set present on every inquiry from the moment
// it's created, plus any additional documents requested later (e.g. by
// Operations on the Job page once one exists) — see
// POST /commercial/inquiries/{iid}/trade-documents.
// Reuses MissingRequirementList's exact multi-select "ask/upload/send one
// combined request" UI (extraItems/includeExtraInCount/etc props added
// specifically so this second use doesn't require a second component) rather
// than building a parallel checklist widget.
function TradeDocumentsSection({ inquiryId, customerId, onSendDocumentRequest, onPreview }) {
  const { data } = useInquiryTradeDocuments(inquiryId);
  const qc = useQueryClient();
  const [newDocName, setNewDocName] = useState("");
  const [newDocRequired, setNewDocRequired] = useState(true);
  const [addBusy, setAddBusy] = useState(false);
  const rows = data?.checklist || [];
  const items = rows.map((r) => ({
    field: `trade_${r.name.replace(/[^a-zA-Z0-9]+/g, "_").toLowerCase()}`,
    label: r.name,
    blocking: r.required,
    optional: !r.required,
    uploadCategory: "TRADE",
    prompt: r.source === "additional"
      ? `Requested by ${r.requestedByRole ? r.requestedByRole[0].toUpperCase() + r.requestedByRole.slice(1) : "team"}${r.requestedBy ? ` · ${r.requestedBy}` : ""}`
      : undefined,
  }));
  // Documents already resolved (submitted) — passed to MissingRequirementList
  // as its `documents` match source, keyed the same way the backend matches
  // (loose type-name substring match), so Submitted/Pending here always
  // agrees with the backend checklist.
  const resolvedDocs = rows.filter((r) => r.submitted).map((r) => ({ type: r.name, id: r.documentId, date: r.uploadedAt, review: r.verification }));

  const addDocument = async () => {
    const name = newDocName.trim();
    if (!name) return;
    setAddBusy(true);
    try {
      await api.post(`/commercial/inquiries/${inquiryId}/trade-documents`, { names: [name], required: newDocRequired });
      await qc.invalidateQueries({ queryKey: ["inquiry-trade-docs", inquiryId] });
      setNewDocName("");
      toast.success(`"${name}" added to the Trade Documents checklist`);
    } catch (err) { toast.error(apiErrorMessage(err, "Could not add document")); }
    finally { setAddBusy(false); }
  };

  return (
    <div data-testid="trade-documents-section">
      <MissingRequirementList
        blockers={[]}
        extraItems={items}
        includeExtraInCount
        blockingLabel="Required"
        cardTitle="Trade Documents"
        emptyLabel="All mandatory trade documents are on file."
        testId="trade-documents"
        onSendDocumentRequest={onSendDocumentRequest}
        customerId={customerId}
        inquiryId={inquiryId}
        documents={resolvedDocs}
        onPreview={onPreview}
      />
      <div className="mt-2 flex items-center gap-2 rounded-lg border border-dashed border-slate-200 bg-white px-3 py-2" data-testid="trade-doc-add-row">
        <Input value={newDocName} onChange={(e) => setNewDocName(e.target.value)} placeholder="Add an additional document for this trade…"
          className="h-7 flex-1 text-xs" data-testid="trade-doc-add-input" />
        <label className="flex items-center gap-1.5 text-[11px] text-slate-500 shrink-0">
          <input type="checkbox" className="h-3.5 w-3.5" checked={newDocRequired} onChange={(e) => setNewDocRequired(e.target.checked)} data-testid="trade-doc-add-required" />
          Required
        </label>
        <Button size="sm" variant="outline" className="h-7 text-xs shrink-0" disabled={!newDocName.trim() || addBusy}
          onClick={addDocument} data-testid="trade-doc-add-btn">
          {addBusy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null}Add
        </Button>
      </div>
    </div>
  );
}

// ---- Feasibility ----
const FEAS_LABEL = { PASS: "Ready for rate", CONDITIONAL: "Needs review", FAIL: "Not serviceable" };
function FeasibilityCard({ feasibility, inquiry, onRun, busy }) {
  if (!inquiry) return null;
  const stage = ["READY_FOR_FEASIBILITY", "FEASIBILITY_REVIEW", "READY_FOR_RATE", "RATE_REQUESTED", "QUOTED", "WON"].includes(inquiry.status);
  if (!stage && !feasibility) return null;
  const verdict = feasibility?.verdict;
  const label = inquiry.dg || inquiry.battery || inquiry.restricted || inquiry.temperatureControlled
    ? (verdict ? FEAS_LABEL[verdict] : "Customer information required")
    : (verdict ? FEAS_LABEL[verdict] : "Ready for rate");
  return (
    <Card title="Feasibility" testId="feasibility-card" tone={verdict === "FAIL" ? "danger" : verdict === "CONDITIONAL" ? "warn" : "default"}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-slate-800">{label}</div>
          {feasibility?.reasons?.length > 0 && <div className="text-[12px] text-slate-500 mt-0.5">{feasibility.reasons.join("; ")}</div>}
        </div>
        {!feasibility && (
          <Button size="sm" className="h-8" disabled={busy} onClick={onRun} data-testid="run-feasibility-btn">
            <Sparkles className="h-3.5 w-3.5 mr-1.5" />Run feasibility check
          </Button>
        )}
      </div>
    </Card>
  );
}

// ---- Rate sourcing / comparison ----
export function RateComparison({ rateRequest, supplierRates, selectedSupplierRate, seeCosts, onSelect, busy }) {
  if (!rateRequest) return null;
  const received = supplierRates.length;
  return (
    <Card title="Supplier rates" testId="rate-comparison">
      <div className="text-[13px] text-slate-600 mb-2.5">
        Rate request recorded for {rateRequest.supplierRecipients?.length || 0} supplier(s) · {received} rate{received !== 1 ? "s" : ""} received
        {selectedSupplierRate && ` · selected ${selectedSupplierRate.id}, expires ${(selectedSupplierRate.validUntil || "").slice(0, 10)}`}
      </div>
      {!seeCosts ? (
        <div className="flex items-center gap-2 text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
          <Lock className="h-4 w-4 shrink-0" />Supplier rate detail is restricted for your role.
        </div>
      ) : received === 0 ? (
        <div className="text-[13px] text-slate-400">No supplier rates received yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                <th className="text-left font-semibold py-1.5 pr-2">Supplier</th>
                <th className="text-right font-semibold py-1.5 px-2">Total buy</th>
                <th className="text-left font-semibold py-1.5 px-2">Currency</th>
                <th className="text-left font-semibold py-1.5 px-2">Validity</th>
                <th className="text-left font-semibold py-1.5 px-2">Status</th>
                <th className="py-1.5 pl-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {supplierRates.map((r) => {
                const chg = r.charges || {};
                const known = Object.values(chg).every((v) => v !== null && v !== undefined);
                const total = known ? Object.values(chg).reduce((a, b) => a + Number(b || 0), 0) : null;
                const selected = selectedSupplierRate?.id === r.id;
                return (
                  <tr key={r.id} data-testid={`rate-row-${r.id}`} className={selected ? "bg-indigo-50/60" : ""}>
                    <td className="py-2 pr-2 font-medium text-slate-800">{r.supplierId}</td>
                    <td className="py-2 px-2 text-right font-mono-num">{total !== null ? total.toLocaleString() : <span className="text-amber-600">Unknown charge</span>}</td>
                    <td className="py-2 px-2 text-slate-600">{r.currency}</td>
                    <td className="py-2 px-2 text-slate-600">
                      {(r.validUntil || "").slice(0, 10)}
                      {r.expired && <span className="ml-1.5 text-[10px] font-semibold text-red-700 bg-red-100 rounded px-1.5 py-0.5">Expired</span>}
                    </td>
                    <td className="py-2 px-2"><StatusPill status={r.reviewStatus || "Pending"} /></td>
                    <td className="py-2 pl-2 text-right">
                      {selected ? (
                        <span className="text-[11px] font-semibold text-indigo-600">Selected</span>
                      ) : (
                        <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy || r.expired}
                          onClick={() => onSelect(r)} data-testid={`select-rate-${r.id}`}>
                          {r.expired ? "Expired" : "Validate & select"}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

export function RfqDispatchPanel({ rateRequest, readiness, onDispatch, busy }) {
  if (!rateRequest) return null;
  if (!readiness) {
    return <Card title="Vendor RFQ delivery" testId="rfq-dispatch-loading"><div className="text-sm text-slate-500">Checking SMTP connection and supplier recipients…</div></Card>;
  }
  const results = readiness.dispatchResults || [];
  return (
    <Card title="Vendor RFQ delivery" testId="rfq-dispatch-panel" tone={readiness.status === "PARTIALLY_DISPATCHED" || readiness.status === "DISPATCH_FAILED" ? "warn" : "default"}
      right={<StatusPill status={readiness.status === "RECORDED" ? "Recorded · not sent" : readiness.status} />}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <div className="text-sm font-medium text-slate-800">{readiness.message}</div>
          <div className="mt-1 text-xs text-slate-500">{readiness.validRecipientCount} valid email recipient(s) out of {readiness.selectedCount} recorded supplier(s).</div>
        </div>
        {readiness.canDispatch && !readiness.alreadyDispatched && (
          <Button onClick={onDispatch} disabled={busy} className="shrink-0" data-testid="dispatch-rfq-btn">
            {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Mail className="mr-1.5 h-3.5 w-3.5" />}Send RFQ emails
          </Button>
        )}
      </div>
      {results.length > 0 && (
        <div className="mt-3 divide-y divide-slate-100 rounded-md border border-slate-200 bg-white">
          {results.map((result) => (
            <div key={result.supplierId} className="flex items-start justify-between gap-3 px-3 py-2 text-xs" data-testid={`rfq-result-${result.supplierId}`}>
              <div className="min-w-0"><div className="font-medium text-slate-700">{result.supplierName}</div><div className="truncate text-slate-400">{result.email || result.failureReason || "No eligible email"}</div></div>
              <StatusPill status={result.status} />
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ---- Quotation ----
export function QuotationSummary({ quotation, versions, currentVersion, approval, onSubmitApproval, onSend, onOpenPdf, busy, canSend }) {
  if (!quotation) return null;
  const expired = currentVersion?.validUntil && new Date(currentVersion.validUntil) < new Date();
  return (
    <Card title="Quotation" testId="quotation-summary">
      {currentVersion ? (
        <>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="text-lg font-display font-bold text-slate-900 font-mono-num">
                {money(currentVersion.total, currentVersion.currency)} <span className="text-sm font-normal text-slate-400">V{currentVersion.version}</span>
              </div>
              <div className="text-[12px] text-slate-500 mt-0.5">
                {currentVersion.paymentTerms} · Valid until {(currentVersion.validUntil || "").slice(0, 10)}
                {currentVersion.transit && ` · ${currentVersion.transit}`}
              </div>
            </div>
            <StatusPill status={expired ? "Expired" : currentVersion.sentAt ? "Issued" : currentVersion.approvalStatus === "APPROVED" ? "Approved" : "Pending Approval"} />
          </div>

          {expired && (
            <div className="mt-3 flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="quotation-expired-warning">
              <AlertTriangle className="h-4 w-4 shrink-0" />Quotation expired. Re-rate/revision required — this offer can no longer be issued or accepted as-is.
            </div>
          )}

          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <Button size="sm" variant="outline" className="h-8" onClick={() => onOpenPdf(currentVersion)} data-testid="open-quotation-pdf">
              <FileText className="h-3.5 w-3.5 mr-1.5" />Preview PDF
            </Button>
            {currentVersion.approvalStatus !== "APPROVED" && !currentVersion.sentAt && (
              <Button size="sm" disabled={busy} onClick={onSubmitApproval} data-testid="submit-approval-btn">Submit for approval</Button>
            )}
            {currentVersion.approvalStatus === "APPROVED" && !currentVersion.sentAt && !expired && canSend && (
              <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" disabled={busy} onClick={() => onSend(currentVersion)} data-testid="send-quotation-btn">
                Issue quotation record
              </Button>
            )}
            {currentVersion.approvalStatus !== "APPROVED" && (
              <span className="text-[12px] text-slate-400" data-testid="send-disabled-not-approved">Issue is unavailable until this version is approved.</span>
            )}
          </div>
          {approval && approval.status === "Pending" && (
            <div className="mt-2 text-[12px] text-amber-700">Approval {approval.id} pending — requires {approval.requiredRole}.</div>
          )}
          {currentVersion.sentAt && (
            <div className="mt-3 pt-3 border-t border-slate-100 text-[13px] text-slate-600" data-testid="quotation-followup">
              Issued in the dashboard {(currentVersion.sentAt || "").slice(0, 10)} for {currentVersion.sentChannel}.
              {" "}External delivery is not confirmed; connect an approved provider before treating this as sent.
            </div>
          )}
        </>
      ) : (
        <div className="text-[13px] text-slate-400">No quotation version yet.</div>
      )}

      {versions.length > 1 && (
        <div className="mt-3 pt-3 border-t border-slate-100">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">Version history</div>
          <div className="space-y-1">
            {[...versions].reverse().map((v) => (
              <div key={v.version} data-testid={`qv-row-${v.version}`} className="flex items-center justify-between text-[13px]">
                <span className={v.isCurrent ? "font-semibold text-slate-800" : "text-slate-400"}>
                  {quotation.id}-V{v.version} {v.isCurrent ? "— CURRENT" : "— SUPERSEDED"}
                </span>
                <span className="font-mono-num text-slate-500">{money(v.total, v.currency)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

// ---- Acceptance / intent ----
const INTENT_LABEL = {
  DETECTED: "Customer Accepted (Sales-confirmed) — Pending Send to Finance",
  PENDING_VERIFICATION: "Customer Accepted (Sales-confirmed) — Pending Send to Finance",
  VERIFIED: "Verified — Ready for Finance Review",
  INVALIDATED_BY_CHANGE: "Scope changed since detection — revision required",
  REJECTED_AS_ACCEPTANCE: "Not an acceptance",
  SUPERSEDED: "Superseded — a different version was confirmed as accepted",
};

export function AcceptanceReview({ acceptance, currentVersion, versions, financeSummary, onVerify, busy, client, hideVerifyAction = false, documents = [] }) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  if (!acceptance) return null;
  const needsVerification = ["DETECTED", "PENDING_VERIFICATION"].includes(acceptance.status);
  // Aug 2026 — an acceptance may reference an ad-hoc-uploaded quotation
  // document instead of a structured quotation_versions record (see
  // detect_adhoc_quotation_acceptance in services/commercial.py) — there's
  // no version/scope snapshot to compare against for those.
  const isAdHoc = !acceptance.quotationId && !!acceptance.documentId;
  // The customer may have accepted a version that is no longer `currentVersion`
  // (an older sent version) — show/confirm against the ACTUAL accepted
  // version's own snapshot, not whichever version happens to be current.
  // `POST /acceptances/{id}/verify` itself fails closed with
  // SCOPE_CHANGED_REQUOTE_REQUIRED in that case (services/commercial.py
  // verify_customer_acceptance) — this just keeps the on-screen numbers honest.
  const acceptedVersionObj = isAdHoc ? null : ((versions || []).find((v) => v.version === acceptance.quotationVersion) || currentVersion);
  const acceptedDoc = isAdHoc ? documents.find((d) => d.id === acceptance.documentId) : null;
  const candidateLabel = isAdHoc ? (acceptedDoc?.name || acceptance.documentId) : `${acceptance.quotationId} V${acceptance.quotationVersion}`;
  const isStaleVersion = !isAdHoc && currentVersion && acceptedVersionObj && acceptedVersionObj.version !== currentVersion.version;
  const confirmAndVerify = async () => { await onVerify(acceptedVersionObj); setConfirmOpen(false); };
  return (
    <Card
      title="Customer intent & acceptance"
      testId="acceptance-review"
      tone={acceptance.status === "VERIFIED" ? "default" : needsVerification ? "warn" : "danger"}
    >
      <div className="text-sm font-semibold text-slate-800">{INTENT_LABEL[acceptance.status] || acceptance.status}</div>
      <div className="text-[12px] text-slate-500 mt-1">
        Evidence: {acceptance.evidenceType} via {acceptance.source} {acceptance.detectedByAI && "· AI-detected (not final until verified by a human)"}
      </div>
      {isAdHoc && (
        <div className="text-[11px] text-indigo-600 mt-0.5" data-testid="acceptance-adhoc-badge">
          Ad-hoc quotation document (uploaded outside the quotation builder) — no structured charge lines available.
        </div>
      )}
      {acceptance.evidenceRef && (
        <div className="mt-1.5 rounded-md border border-slate-200 bg-slate-50 p-2 text-[12px] text-slate-600" data-testid="acceptance-evidence-text">
          "{acceptance.evidenceRef}"
        </div>
      )}

      {isStaleVersion && (
        <div className="mt-2 text-[12px] text-amber-700" data-testid="acceptance-stale-version-warning">
          A newer quotation version (V{currentVersion.version}) now exists — this acceptance was confirmed against V{acceptedVersionObj.version} and cannot be sent to Finance until re-confirmed against the current version.
        </div>
      )}

      {needsVerification && (acceptedVersionObj || isAdHoc) && (
        <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3 space-y-1.5" data-testid="acceptance-verify-panel">
          <div className="text-[12px] text-slate-500">Confirm this customer is accepting this exact offer:</div>
          <div className="text-sm">
            {isAdHoc
              ? <><b>{candidateLabel}</b> · {acceptance.acceptedAmount != null ? money(acceptance.acceptedAmount, acceptance.currency) : "amount: see attached document"}</>
              : <><b>{acceptance.quotationId}</b> V{acceptance.quotationVersion} · {money(acceptedVersionObj.total, acceptedVersionObj.currency)} · {acceptedVersionObj.paymentTerms}</>}
          </div>
          {hideVerifyAction ? (
            // Aug 2026 IA correction: embedded in Customer360, this action
            // lives once, persistently, in the top-right "Hand off to
            // Finance" button — no second trigger location on the same
            // screen.
            <div className="text-[12px] text-indigo-700 font-medium" data-testid="verify-acceptance-relocated-note">
              Use "Send to Finance for Review" at the top of this page to confirm and hand this order off.
            </div>
          ) : (
            <>
              <Button size="sm" className="mt-2 bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={() => setConfirmOpen(true)} data-testid="verify-acceptance-btn">
                <ShieldAlert className="h-3.5 w-3.5 mr-1.5" />Verify this exact {isAdHoc ? "document" : "version"} &amp; scope
              </Button>
              <HandoffConfirmDialog
                open={confirmOpen}
                onOpenChange={setConfirmOpen}
                busy={busy}
                title="Hand off to Finance"
                confirmLabel="Confirm and hand off to Finance"
                summaryRows={[
                  { label: "Customer", value: client?.name || acceptance.clientId },
                  { label: "Quotation", value: candidateLabel },
                  { label: "Amount", value: isAdHoc ? (acceptance.acceptedAmount != null ? money(acceptance.acceptedAmount, acceptance.currency) : "See attached quotation document") : money(acceptedVersionObj.total, acceptedVersionObj.currency) },
                  { label: "Payment terms", value: isAdHoc ? "See attached quotation document" : acceptedVersionObj.paymentTerms },
                  { label: "Evidence", value: `${acceptance.evidenceType} via ${acceptance.source}` },
                ]}
                receivingTeamSees={[
                  "The full commercial snapshot: quotation, charges, payment terms and acceptance evidence.",
                  "Finance will run credit/advance checks and release this order for Job creation from here — Sales work on this order stops at handoff.",
                ]}
                onConfirm={confirmAndVerify}
              />
            </>
          )}
        </div>
      )}

      {acceptance.status === "INVALIDATED_BY_CHANGE" && (
        <div className="mt-2 text-[12px] text-red-700">The offer changed since this acceptance was detected — a revised quotation is required before verification can proceed.</div>
      )}

      {acceptance.status === "VERIFIED" && (
        <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3" data-testid="ready-for-finance">
          <div className="text-sm font-semibold text-emerald-800">READY FOR FINANCE REVIEW</div>
          <div className="text-[12px] text-emerald-700 mt-0.5">Finance status: {financeSummary?.financeStatus || "Ready for Finance review"}.</div>
          {financeSummary?.customerFacingAmountRequired != null && (
            <div className="text-[12px] text-emerald-700 mt-0.5">Amount required from customer: {financeSummary.customerFacingAmountRequired}</div>
          )}
          <div className="text-[11px] text-emerald-600 mt-1">Sales work on this order stops here — Finance owns the next step.</div>
        </div>
      )}
    </Card>
  );
}

function WorkflowAction({ title, description, action, onClick, busy }) {
  return <Card testId={`workflow-action-${action.toLowerCase().replace(/[^a-z]+/g, "-")}`}><div className="flex items-center justify-between gap-4"><div><div className="text-sm font-semibold text-slate-800">{title}</div><div className="mt-0.5 text-xs text-slate-500">{description}</div></div><Button size="sm" onClick={onClick} disabled={busy} className="shrink-0">{busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}{action}</Button></div></Card>;
}

function RateRequestDialog({ open, onOpenChange, vendors, selected, onSelected, onSubmit, busy }) {
  const toggle = (id) => onSelected(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  return <Dialog open={open} onOpenChange={onOpenChange}><DialogContent className="sm:max-w-lg"><DialogHeader><DialogTitle>Record RFQ recipients</DialogTitle></DialogHeader><div className="max-h-72 space-y-2 overflow-y-auto">{vendors.length ? vendors.map((vendor) => <label key={vendor.id} className="flex cursor-pointer items-center gap-3 rounded-md border border-slate-200 p-3 hover:bg-slate-50"><input type="checkbox" checked={selected.includes(vendor.id)} onChange={() => toggle(vendor.id)} className="h-4 w-4" /><div><div className="text-sm font-medium text-slate-800">{vendor.name}</div><div className="text-xs text-slate-500">{vendor.id}{vendor.modes?.length ? ` · ${vendor.modes.join(", ")}` : ""}</div></div></label>) : <div className="text-sm text-slate-500">No approved suppliers are available.</div>}</div><div className="text-xs text-slate-500">This records the intended recipients and starts rate tracking. External email or vendor-portal delivery requires an approved provider integration.</div><DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button><Button onClick={onSubmit} disabled={busy || !selected.length}>Record rate request</Button></DialogFooter></DialogContent></Dialog>;
}

const RATE_FIELDS = [["freight", "Freight *"], ["originCharges", "Origin charges"], ["destinationCharges", "Destination charges"], ["transport", "Transport"], ["customsClearance", "Customs clearance"], ["otherSurcharges", "Other surcharges"]];
function SupplierRateDialog({ open, onOpenChange, vendors, value, onChange, onSubmit, busy }) {
  const set = (key) => (event) => onChange({ ...value, [key]: event.target.value });
  return <Dialog open={open} onOpenChange={onOpenChange}><DialogContent className="sm:max-w-2xl"><DialogHeader><DialogTitle>Record supplier rate</DialogTitle></DialogHeader><div className="grid gap-3 sm:grid-cols-3"><div className="sm:col-span-2"><Label>Supplier *</Label><select value={value.supplierId} onChange={set("supplierId")} className="mt-1 h-9 w-full rounded-md border border-input bg-white px-3 text-sm"><option value="">Select supplier</option>{vendors.map((vendor) => <option key={vendor.id} value={vendor.id}>{vendor.name}</option>)}</select></div><div><Label>Currency</Label><Input value={value.currency} onChange={set("currency")} className="mt-1" /></div>{RATE_FIELDS.map(([key, label]) => <div key={key}><Label>{label}</Label><Input type="number" min="0" step="0.01" value={value[key]} onChange={set(key)} className="mt-1" /></div>)}<div><Label>Transit days</Label><Input type="number" min="0" value={value.transitDays} onChange={set("transitDays")} className="mt-1" /></div><div><Label>Valid until *</Label><Input type="date" value={value.validUntil} onChange={set("validUntil")} className="mt-1" /></div></div><div className="text-xs text-slate-500">Leave a charge blank only when the vendor explicitly does not include it. Values are preserved for audit and comparison.</div><DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button><Button onClick={onSubmit} disabled={busy}>Save supplier rate</Button></DialogFooter></DialogContent></Dialog>;
}

function QuotationBuilderDialog({ open, onOpenChange, lines, onLines, terms, onTerms, onSubmit, busy, currency }) {
  const lineLabel = (key) => RATE_FIELDS.find(([id]) => id === key)?.[1].replace(" *", "") || key.replace(/([A-Z])/g, " $1");
  return <Dialog open={open} onOpenChange={onOpenChange}><DialogContent className="sm:max-w-2xl"><DialogHeader><DialogTitle>Build customer quotation</DialogTitle></DialogHeader><div className="space-y-4"><div><div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Customer selling charges · {currency}</div><div className="grid gap-3 sm:grid-cols-3">{Object.entries(lines).map(([key, value]) => <div key={key}><Label>{lineLabel(key)}</Label><Input type="number" min="0" step="0.01" value={value} onChange={(event) => onLines({ ...lines, [key]: event.target.value })} className="mt-1" /></div>)}</div></div><div className="grid gap-3 sm:grid-cols-3"><div><Label>Payment terms</Label><Input value={terms.paymentTerms} onChange={(e) => onTerms({ ...terms, paymentTerms: e.target.value })} className="mt-1" /></div><div><Label>Validity (days)</Label><Input type="number" min="1" value={terms.validityDays} onChange={(e) => onTerms({ ...terms, validityDays: e.target.value })} className="mt-1" /></div><div><Label>Transit time</Label><Input value={terms.transit} onChange={(e) => onTerms({ ...terms, transit: e.target.value })} placeholder="e.g. 4–6 days" className="mt-1" /></div><div><Label>Inclusions (one per line)</Label><Textarea rows={3} value={terms.inclusions} onChange={(e) => onTerms({ ...terms, inclusions: e.target.value })} className="mt-1" /></div><div><Label>Exclusions (one per line)</Label><Textarea rows={3} value={terms.exclusions} onChange={(e) => onTerms({ ...terms, exclusions: e.target.value })} className="mt-1" /></div></div></div><DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button><Button onClick={onSubmit} disabled={busy || !Object.keys(lines).length}>Create quotation version</Button></DialogFooter></DialogContent></Dialog>;
}

// ==========================================================================
// Aug 2026 — Quotation acceptance confirmation workflow.
//
// Sales may send several quotation versions to the same customer over
// WhatsApp/Email; Sales opens this dialog and explicitly selects — never
// auto-detected — which sent version (or ad-hoc "Final Quotation" upload
// document, via `docCandidates`) the customer accepted.
//
// Demo-phase simplification (product owner's explicit instruction): no
// WhatsApp/email/call message evidence or AI matching is required — Sales'
// manual selection + confirmation IS the acceptance evidence. Real
// message-based/AI-assisted detection is deferred to the production phase.
// `createdBy`/`createdAt` on the resulting customer_acceptances record (set
// server-side from the authenticated actor,
// see detect_customer_acceptance / detect_adhoc_quotation_acceptance in
// services/commercial.py) already captures who approved it and when, so
// nothing here needs to fabricate an evidence string.
function ConfirmAcceptanceDialog({ open, onOpenChange, quotation, versions, docCandidates, initialDocId, onConfirm, onOpenPdf, onOpenDoc, busy }) {
  const sentVersions = (versions || []).filter((v) => v.sentAt).sort((a, b) => b.version - a.version);
  const docs = docCandidates || [];
  const [selection, setSelection] = useState(null); // {kind:"version", version} | {kind:"doc", documentId}

  useEffect(() => {
    if (!open) { setSelection(null); return; }
    if (initialDocId) setSelection({ kind: "doc", documentId: initialDocId });
    // Reset selection whenever the dialog (re)opens — never carry a stale
    // radio pick across a different open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const chosenVersion = selection?.kind === "version" ? sentVersions.find((v) => v.version === selection.version) : null;
  const chosenDoc = selection?.kind === "doc" ? docs.find((d) => d.documentId === selection.documentId) : null;
  const canConfirm = !!chosenVersion || !!chosenDoc;

  const submit = () => {
    if (!canConfirm) return;
    onConfirm({ version: chosenVersion, doc: chosenDoc });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Mark quotation as customer accepted</DialogTitle>
          <DialogDescription>Select which quotation the customer accepted, then confirm — Sales makes the final call.</DialogDescription>
        </DialogHeader>

        {!sentVersions.length && !docs.length ? (
          <div className="text-sm text-slate-500">No quotation has been sent to this customer yet.</div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label>Select accepted quotation *</Label>
              <div className="mt-1.5 space-y-2">
                {sentVersions.length > 0 && (
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Structured quotation versions</div>
                    <div className="space-y-2">
                      {sentVersions.map((v) => (
                        <label key={v.version} className={`flex cursor-pointer items-center justify-between gap-3 rounded-md border p-2.5 ${selection?.kind === "version" && selection.version === v.version ? "border-indigo-400 bg-indigo-50/50" : "border-slate-200 hover:bg-slate-50"}`} data-testid={`acceptance-version-option-${v.version}`}>
                          <div className="flex items-center gap-2.5">
                            <input type="radio" name="acceptance-candidate" className="h-4 w-4" checked={selection?.kind === "version" && selection.version === v.version} onChange={() => setSelection({ kind: "version", version: v.version })} />
                            <div>
                              <div className="text-sm font-medium text-slate-800">
                                {quotation?.id} V{v.version} — {money(v.total, v.currency)}
                              </div>
                              <div className="text-[11px] text-slate-500">Sent {(v.sentAt || "").slice(0, 10)} via {v.sentChannel} · {v.paymentTerms}</div>
                            </div>
                          </div>
                          <Button size="sm" variant="outline" className="h-7 px-2 text-[11px] shrink-0" onClick={(e) => { e.preventDefault(); onOpenPdf(v); }}>
                            <FileText className="h-3 w-3 mr-1" />PDF
                          </Button>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                {docs.length > 0 && (
                  <div className={sentVersions.length > 0 ? "pt-1" : ""}>
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Ad-hoc quotation documents (uploaded outside the quotation builder)</div>
                    <div className="space-y-2">
                      {docs.map((d) => (
                        <label key={d.documentId} className={`flex cursor-pointer items-center justify-between gap-3 rounded-md border p-2.5 ${selection?.kind === "doc" && selection.documentId === d.documentId ? "border-indigo-400 bg-indigo-50/50" : "border-slate-200 hover:bg-slate-50"}`} data-testid={`acceptance-doc-option-${d.documentId}`}>
                          <div className="flex items-center gap-2.5">
                            <input type="radio" name="acceptance-candidate" className="h-4 w-4" checked={selection?.kind === "doc" && selection.documentId === d.documentId} onChange={() => setSelection({ kind: "doc", documentId: d.documentId })} />
                            <div>
                              <div className="text-sm font-medium text-slate-800">{d.name}</div>
                              <div className="text-[11px] text-slate-500">Sent {(d.sentAt || "").slice(0, 10)}</div>
                            </div>
                          </div>
                          {onOpenDoc && (
                            <Button size="sm" variant="outline" className="h-7 px-2 text-[11px] shrink-0" onClick={(e) => { e.preventDefault(); onOpenDoc(d); }}>
                              <FileText className="h-3 w-3 mr-1" />View
                            </Button>
                          )}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="text-xs text-slate-500">
              This records your confirmation, along with who confirmed it and when. Any other sent version or document with a pending acceptance record will be marked Superseded. A separate "Send to Finance for Review" step is still required after this.
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !canConfirm} data-testid="confirm-acceptance-btn">Mark as Customer Accepted</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==========================================================================
// Aug 2026 IA correction — an Inquiry is never its own page. This component
// (still exported as `Workspace` for the direct/standalone test-only usage
// below) is rendered EMBEDDED inside Customer360, inline within the client
// page, whenever a specific inquiry is selected from that client's list.
// `embedded` suppresses the page-level chrome that would duplicate what
// Customer360 already shows above it (client name/back button, the
// call/WhatsApp/email header actions, the sibling-inquiries strip — the
// client page already renders the inquiry list itself) and reports the
// real handoff-to-Finance eligibility upward via `onHandoffState` so
// Customer360 can render one persistent action in its own page header
// instead of the action only being reachable by scrolling into this panel.
export default function Workspace({ embedded = false, inquiryId: propInquiryId, onSelectSibling, onHandoffState } = {}) {
  const { id: paramId } = useParams();
  const id = propInquiryId || paramId;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { openComposer } = useApp();
  const { data: me } = useMe();
  const { data, isLoading, error, refetch } = useCommercialWorkspace(id);
  const [busy, setBusy] = useState(false);
  const [upload, setUpload] = useState(false);
  const [activeDoc, setActiveDoc] = useState(null);
  const [docOpen, setDocOpen] = useState(false);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [shareQuotationUploading, setShareQuotationUploading] = useState(false);
  const [activeQuotationDoc, setActiveQuotationDoc] = useState(null);
  const [interestedConfirmOpen, setInterestedConfirmOpen] = useState(false);
  const [rateRequestOpen, setRateRequestOpen] = useState(false);
  const [rateSupplierIds, setRateSupplierIds] = useState([]);
  const [supplierRateOpen, setSupplierRateOpen] = useState(false);
  const [rateEntry, setRateEntry] = useState({ supplierId: "", currency: "AED", freight: "", originCharges: "", destinationCharges: "", transport: "", customsClearance: "", otherSurcharges: "", validUntil: "", transitDays: "" });
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteLines, setQuoteLines] = useState({});
  const [quoteTerms, setQuoteTerms] = useState({ paymentTerms: "30 days", validityDays: "7", transit: "", inclusions: "", exclusions: "" });
  const [acceptanceOpen, setAcceptanceOpen] = useState(false);
  // Aug 2026 — pre-selects one specific ad-hoc document when the dialog is
  // opened from that document's own "Confirm Accepted" button in the Final
  // Quotation card, instead of the generic "Confirm Customer Accepted a
  // Quotation" action.
  const [acceptanceInitialDocId, setAcceptanceInitialDocId] = useState(null);
  const [requirementsOpen, setRequirementsOpen] = useState(false);
  const [requirementsValue, setRequirementsValue] = useState(null);

  const clientId = data?.inquiry?.clientId;
  const { data: comm } = useCommunications(clientId);
  const { data: docsData } = useCustomerDocuments(clientId);
  const { data: inquiryDocs } = useInquiryDocuments(id);
  const { data: vendors } = useVendors();
  // Item 3 — client context: every other inquiry for this same client,
  // chronological, so opening one inquiry never loses the client picture.
  const { data: clientRecord } = useCustomer(clientId);
  const rateRequestId = data?.rateRequest?.id;
  const { data: rfqDispatchReadiness, refetch: refetchRfqDispatch } = useRateRequestDispatchReadiness(rateRequestId);

  // Aug 2026 IA correction — item 3: report the real, server-derived
  // handoff-to-Finance eligibility upward so Customer360 can render a
  // persistent top-right action. Uses the exact same gate as the inline
  // "Verify acceptance" trigger in AcceptanceReview below — a possible
  // acceptance detected (DETECTED/PENDING_VERIFICATION) against a real
  // quotation version. Once VERIFIED the handoff has already happened.
  // Placed before the loading/error early-returns (rules of hooks — every
  // hook in this component must run on every render).
  const hoAcceptance = data?.acceptance;
  // Aug 2026 — an acceptance may reference an ad-hoc-uploaded quotation
  // document instead of a structured quotation_versions record (see
  // detect_adhoc_quotation_acceptance in services/commercial.py / the
  // "Confirm Accepted" button on each Final Quotation document below).
  const hoIsAdHoc = hoAcceptance && !hoAcceptance.quotationId && !!hoAcceptance.documentId;
  // Finance must receive EXACTLY the version Sales confirmed the customer
  // accepted — which is not always `currentVersion` (the customer may have
  // accepted an earlier sent version before a revision went out). Look up
  // the acceptance's own referenced version from the full version list
  // rather than assuming "current". No such lookup exists for an ad-hoc
  // acceptance — there is no versions list to search.
  const hoAcceptedVersion = hoIsAdHoc ? null : ((data?.versions || []).find((v) => v.version === hoAcceptance?.quotationVersion) || data?.currentVersion);
  const hoDocRows = Array.isArray(inquiryDocs) ? inquiryDocs : [];
  const hoAcceptedDoc = hoIsAdHoc ? hoDocRows.find((d) => d.id === hoAcceptance.documentId) : null;
  const hoClient = data?.client;
  useEffect(() => {
    if (!onHandoffState) return;
    const needsVerification = hoAcceptance && ["DETECTED", "PENDING_VERIFICATION"].includes(hoAcceptance.status);
    if (needsVerification && (hoAcceptedVersion || hoIsAdHoc)) {
      onHandoffState({
        eligible: true,
        alreadyDone: false,
        reason: null,
        busy,
        // Item 9 — label matches Customer360's top-of-page button text.
        title: "Send to Finance for Review",
        confirmLabel: "Confirm and send to Finance for review",
        summaryRows: hoIsAdHoc ? [
          { label: "Customer", value: hoClient?.name || hoAcceptance.clientId },
          { label: "Quotation", value: hoAcceptedDoc?.name || hoAcceptance.documentId },
          { label: "Amount", value: hoAcceptance.acceptedAmount != null ? money(hoAcceptance.acceptedAmount, hoAcceptance.currency) : "See attached quotation document" },
          { label: "Payment terms", value: "See attached quotation document" },
          { label: "Evidence", value: `${hoAcceptance.evidenceType} via ${hoAcceptance.source}` },
        ] : [
          { label: "Customer", value: hoClient?.name || hoAcceptance.clientId },
          { label: "Quotation", value: `${hoAcceptance.quotationId} V${hoAcceptance.quotationVersion}` },
          { label: "Amount", value: money(hoAcceptedVersion.total, hoAcceptedVersion.currency) },
          { label: "Payment terms", value: hoAcceptedVersion.paymentTerms },
          { label: "Evidence", value: `${hoAcceptance.evidenceType} via ${hoAcceptance.source}` },
        ],
        receivingTeamSees: [
          "The full commercial snapshot: quotation, charges, payment terms and acceptance evidence.",
          "Finance will run credit/advance checks and release this order for Job creation from here — Sales work on this order stops at handoff.",
        ],
        onConfirm: async () => {
          setBusy(true);
          try {
            await api.post(`/commercial/acceptances/${hoAcceptance.id}/verify`, {
              expectedVersion: hoAcceptance.version,
              confirmScope: hoIsAdHoc ? {} : { total: hoAcceptedVersion.total, currency: hoAcceptedVersion.currency, paymentTerms: hoAcceptedVersion.paymentTerms },
            });
            await refetch();
          } catch (e) {
            if (isConflict(e)) toast.error("This record was updated by someone else. Refreshing…", { onAutoClose: refetch });
            else toast.error(apiErrorMessage(e));
          } finally { setBusy(false); }
        },
      });
    } else if (hoAcceptance?.status === "VERIFIED") {
      onHandoffState({ eligible: false, alreadyDone: true, reason: "Already handed off to Finance.", busy: false });
    } else {
      onHandoffState({ eligible: false, alreadyDone: false, reason: "Available once acceptance is verified.", busy: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoAcceptance?.id, hoAcceptance?.status, hoAcceptance?.version, hoAcceptedVersion?.version, hoAcceptedDoc?.id, busy, hoClient?.name]);

  // Item 1 — must run on every render (rules of hooks), so declared above
  // the loading/error early returns below. The wrapper references
  // uploadFinalQuotation, which is declared further down with `const` —
  // safe because it's only invoked later (on Submit), after this render
  // has already finished defining it.
  // eslint-disable-next-line no-use-before-define
  const finalQuotationConfirm = useFileSelectConfirm((file) => uploadFinalQuotation(file));

  if (isLoading || !data) return <Loading />;
  if (error) {
    return <EmptyState title="Could not load this record" sub={apiErrorMessage(error, "This inquiry could not be loaded.")} />;
  }

  const { inquiry, client, contact, completeness, blockers, feasibility, rateRequest, supplierRates,
    selectedSupplierRate, quotation, versions, currentVersion, approval, acceptance, nextAction, financeSummary } = data;

  const run = async (fn) => {
    setBusy(true);
    try {
      await fn();
      await refetch();
      return true;
    }
    catch (e) {
      if (isConflict(e)) toast.error("This record was updated by someone else. Refreshing…", { onAutoClose: refetch });
      else toast.error(apiErrorMessage(e));
      return false;
    }
    finally { setBusy(false); }
  };

  const parties = (client?.contacts || []).map((ct) => `${ct.name} · ${ct.role}`);
  const contactLabel = contact?.name || client?.contacts?.[0]?.name || "Primary contact";
  const comms = (channel) => openComposer({
    channel, parties: parties.length ? parties : [contactLabel], defaultParty: parties[0] || contactLabel,
    customerId: clientId, intent: `respond to ${client?.name || "the customer"} about ${inquiry.id}`,
    subjectHint: `[${inquiry.id}] `,
  });

  const askCustomer = (blocker) => openComposer({
    channel: "whatsapp", parties: parties.length ? parties : [contactLabel], defaultParty: parties[0] || contactLabel,
    customerId: clientId, intent: `ask the customer for ${blocker.label.toLowerCase()} for inquiry ${inquiry.id}`,
    subjectHint: `[${inquiry.id}] `,
  });

  // Item 5 — one Composer session for every selected missing document,
  // instead of one session per item. `labels` is the plain-English list
  // built by MissingRequirementList (existing blockers + KYC items + any
  // free-text "Other" entry).
  const requestDocuments = (labels) => openComposer({
    channel: "whatsapp", parties: parties.length ? parties : [contactLabel], defaultParty: parties[0] || contactLabel,
    customerId: clientId,
    intent: `ask the customer for the following documents for inquiry ${inquiry.id}: ${labels.join(", ")}`,
    subjectHint: `[${inquiry.id}] Documents required: ${labels.join(", ")}`,
  });

  const runFeasibility = () => run(() => api.post(`/commercial/inquiries/${inquiry.id}/feasibility`, { expectedVersion: inquiry.version }));
  const transitionInquiry = (toStatus) => run(() => api.post(`/commercial/inquiries/${inquiry.id}/transition`, { toStatus, expectedVersion: inquiry.version }));

  // "Interested" — enabled once every required (blocking) field is
  // submitted. This is a purely Sales-internal flag ("Sales is actively
  // working this customer") — it has NO Finance-facing side effect. See
  // POST /commercial/inquiries/{id}/mark-interested; Finance is only ever
  // involved via the separate, explicit "Send to Finance for Review" action.
  const canMarkInterested = !blockers.some((b) => b.blocking) && !inquiry.customerInterested;
  const markInterested = async () => {
    const success = await run(() => api.post(`/commercial/inquiries/${inquiry.id}/mark-interested`, { expectedVersion: inquiry.version }));
    if (success) { setInterestedConfirmOpen(false); toast.success("Customer marked as interested"); }
  };
  const createRateRequest = async () => {
    if (!rateSupplierIds.length) return toast.error("Select at least one supplier");
    const success = await run(() => api.post(`/commercial/inquiries/${inquiry.id}/rate-requests`, { supplierIds: rateSupplierIds }));
    if (success) { setRateRequestOpen(false); setRateSupplierIds([]); }
  };
  const dispatchRateRequest = async () => {
    let outcome;
    const success = await run(async () => { outcome = (await api.post(`/commercial/rate-requests/${rateRequest.id}/dispatch`)).data; });
    if (success) {
      await refetchRfqDispatch();
      const status = outcome?.rateRequest?.status;
      toast.success(status === "DISPATCHED" ? "RFQ emails accepted for every supplier" : status === "PARTIALLY_DISPATCHED" ? "RFQ dispatch completed with supplier failures" : "RFQ dispatch result recorded");
    }
  };
  const addSupplierRate = async () => {
    if (!rateEntry.supplierId || !rateEntry.validUntil || !rateEntry.freight) return toast.error("Supplier, freight and validity are required");
    const charges = Object.fromEntries(["freight", "originCharges", "destinationCharges", "transport", "customsClearance", "otherSurcharges"].map((key) => [key, rateEntry[key] === "" ? "NOT_INCLUDED" : Number(rateEntry[key])]));
    const success = await run(() => api.post(`/commercial/rate-requests/${rateRequest.id}/supplier-rates`, { supplierId: rateEntry.supplierId, currency: rateEntry.currency, charges, transitDays: rateEntry.transitDays ? Number(rateEntry.transitDays) : undefined, validUntil: `${rateEntry.validUntil}T23:59:59+00:00`, source: "manual" }));
    if (success) {
      setSupplierRateOpen(false);
      setRateEntry({ supplierId: "", currency: "AED", freight: "", originCharges: "", destinationCharges: "", transport: "", customsClearance: "", otherSurcharges: "", validUntil: "", transitDays: "" });
    }
  };
  const openQuoteBuilder = () => {
    const source = selectedSupplierRate?.charges || {};
    setQuoteLines(Object.fromEntries(Object.entries(source).filter(([, value]) => typeof value === "number").map(([key, value]) => [key, String(Math.round(value * 1.2 * 100) / 100)])));
    setQuoteOpen(true);
  };
  const buildQuotation = async () => {
    const labels = { freight: "Freight", originCharges: "Origin charges", destinationCharges: "Destination charges", handling: "Handling", fuel: "Fuel surcharge", security: "Security", transport: "Transport", customsClearance: "Customs clearance", otherSurcharges: "Other surcharges" };
    const chargeLines = Object.entries(quoteLines).filter(([, value]) => value !== "" && Number(value) >= 0).map(([key, value]) => ({ name: labels[key] || key, sell: Number(value) }));
    if (!chargeLines.length) return toast.error("Add at least one customer charge");
    const success = await run(async () => {
      const qid = quotation?.id || (await api.post(`/commercial/inquiries/${inquiry.id}/quotations`)).data.id;
      await api.post(`/commercial/quotations/${qid}/versions`, { supplierRateId: selectedSupplierRate.id, chargeLines, currency: selectedSupplierRate.currency || "AED", taxPct: 0, paymentTerms: quoteTerms.paymentTerms, validityDays: Number(quoteTerms.validityDays) || 7, transit: quoteTerms.transit || undefined, inclusions: quoteTerms.inclusions.split("\n").map((x) => x.trim()).filter(Boolean), exclusions: quoteTerms.exclusions.split("\n").map((x) => x.trim()).filter(Boolean) });
    });
    if (success) setQuoteOpen(false);
  };
  // Sales confirms which SENT version the customer accepted — never
  // hardcoded to currentVersion. Demo-phase (Aug 2026): no message/AI
  // evidence is required — Sales' manual selection + confirmation IS the
  // acceptance evidence (product owner's explicit instruction; real
  // WhatsApp/email/call-based detection is deferred to the production
  // phase). `createdBy`/`createdAt` on the resulting acceptance record are
  // set server-side from the authenticated actor, so who-approved-when is
  // still fully persisted without needing a message reference.
  const confirmAcceptance = async ({ version, doc }) => {
    const evidenceRef = `Confirmed by ${me?.name || "Sales"} — Sales manual confirmation`;
    let success;
    if (version) {
      success = await run(() => api.post(
        `/commercial/quotations/${quotation.id}/versions/${version.version}/acceptance/detect`,
        { source: "sales_manual", evidenceType: "Sales manual confirmation", evidenceRef, detectedByAI: false },
      ));
    } else if (doc) {
      // Aug 2026 — ad-hoc counterpart: the customer accepted a document
      // uploaded+sent via the "Final Quotation" card, not a structured
      // quotation version. See detect_adhoc_quotation_acceptance in
      // services/commercial.py.
      success = await run(() => api.post(
        `/commercial/inquiries/${inquiry.id}/adhoc-quotation-acceptance`,
        { documentId: doc.documentId, source: "sales_manual", evidenceType: "Sales manual confirmation", evidenceRef },
      ));
    }
    if (success) {
      setAcceptanceOpen(false);
      setAcceptanceInitialDocId(null);
      toast.success(version ? `Recorded ${quotation.id} V${version.version} as customer-accepted` : `Recorded "${doc.name}" as customer-accepted`);
    }
  };
  const selectRate = (rate) => run(async () => {
    const v = await api.post(`/commercial/supplier-rates/${rate.id}/validate`, { expectedVersion: rate.version });
    await api.post(`/commercial/inquiries/${inquiry.id}/select-supplier-rate`, { supplierRateId: v.data.id, expectedVersion: inquiry.version });
  });
  const submitApproval = () => run(() => api.post(`/commercial/quotations/${quotation.id}/versions/${currentVersion.version}/submit-approval`));
  const sendQuotation = (v) => {
    const recipients = [...new Set((client?.contacts || []).map((c) => c.email?.trim()).filter(Boolean))];
    if (!recipients.length) {
      toast.error("Add a customer contact email before issuing this quotation record");
      return;
    }
    return run(() => api.post(`/commercial/quotations/${quotation.id}/versions/${v.version}/send`, {
      channel: "email", recipients,
    }));
  };
  const openPdf = async (v) => {
    try {
      const res = await api.get(`/commercial/quotations/${quotation.id}/versions/${v.version}/pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (e) { toast.error("Could not open the quotation PDF"); }
  };
  // `acceptedVersionObj` (passed in by AcceptanceReview) is the quotation
  // version the acceptance actually references — usually `currentVersion`,
  // but not always if the customer accepted an older sent version; the
  // server itself fails closed on a stale version, this just confirms the
  // scope that was actually accepted rather than always assuming "current".
  const verifyAcceptance = (acceptedVersionObj) => run(() => api.post(`/commercial/acceptances/${acceptance.id}/verify`, {
    expectedVersion: acceptance.version,
    confirmScope: {
      total: (acceptedVersionObj || currentVersion).total, currency: (acceptedVersionObj || currentVersion).currency,
      paymentTerms: (acceptedVersionObj || currentVersion).paymentTerms,
    },
  }));

  const isClosed = inquiry.status === "LOST" || inquiry.status === "ON_HOLD_CUSTOMER";
  const canClose = !isClosed && inquiry.status !== "WON";
  // Item 3 (Aug 2026 UX fix): once WON, Sales' part on this specific
  // inquiry is done — the quotation/rate/feasibility/acceptance workflow
  // below is downstream history, not something Sales acts on again.
  // Showing it in full read as "further process" noise. Requirement editing
  // stays available (item 4, a deliberate exception — the customer's
  // shipment details can still change after WON), everything else collapses
  // into one simple status summary that points at where the order stands.
  const isWon = inquiry.status === "WON";
  // Item 4 (Aug 2026 Sales dashboard scope pass, round 2): a customer's
  // requirement can legitimately change after the quotation was accepted —
  // only LOST/EXPIRED are genuinely terminal. Matches the backend guard in
  // services/commercial.py update_inquiry (which also no longer blocks WON).
  // Editing here never touches the approved quotation record.
  const canEditRequirements = !["LOST", "EXPIRED"].includes(inquiry.status);
  const openRequirements = () => {
    setRequirementsValue(requirementDraft(inquiry));
    setRequirementsOpen(true);
  };
  const changeRequirementsOpen = (open) => {
    setRequirementsOpen(open);
    if (!open) setRequirementsValue(null);
  };
  const saveRequirements = async () => {
    const success = await run(() => api.patch(`/commercial/inquiries/${inquiry.id}`, {
      expectedVersion: inquiry.version,
      fields: normalizeRequirementFields(requirementsValue),
    }));
    if (success) {
      toast.success("Shipment requirements updated and blockers recalculated");
      changeRequirementsOpen(false);
    }
    return success;
  };
  const resumeOpportunity = () => run(() => api.post(`/commercial/inquiries/${inquiry.id}/resume`, { expectedVersion: inquiry.version }));
  const reactivateOpportunity = () => run(async () => {
    const res = await api.post(`/commercial/inquiries/${inquiry.id}/reactivate`, {});
    const newId = res.data?.inquiry?.id;
    if (newId) {
      toast.success(`New inquiry ${newId} created.`);
      // Embedded (the normal path, inside Customer360): switch the inline
      // selection to the new inquiry — never a route change. Standalone
      // (direct route, e.g. an old bookmark): fall back to the redirect
      // route, which itself lands on Customer360.
      if (embedded && onSelectSibling) onSelectSibling(newId);
      else navigate(`/sales/inquiries/${newId}`);
    }
  });

  // Item 6 — a quick "attach & share a quotation document" action directly
  // from the client/lead view, distinct from the full versioned quotation
  // builder above: covers the case where Sales wants to quickly send a
  // quotation PDF (initial or a customer-requested revision) without going
  // through the structured QuotationVersion flow. Upload, preview and send
  // are kept as separate, explicit actions rather than one combined
  // upload-and-send step, so Sales can check the file before it goes out.
  const uploadFinalQuotation = async (file) => {
    if (!file) return;
    setShareQuotationUploading(true);
    const fd = new FormData();
    fd.append("file", file); fd.append("docType", "Quotation"); fd.append("jobId", "auto");
    fd.append("category", "TRADE"); fd.append("customerId", clientId || ""); fd.append("inquiryId", inquiry.id);
    try {
      const { data } = await api.post("/documents/upload", fd);
      await Promise.all([qc.invalidateQueries({ queryKey: ["inquiry-docs", inquiry.id] }), qc.invalidateQueries({ queryKey: ["cust-docs", clientId] })]);
      setActiveQuotationDoc(data);
      toast.success("Quotation document uploaded");
    } catch (e) { toast.error(apiErrorMessage(e, "Quotation document upload failed")); throw e; }
    finally { setShareQuotationUploading(false); }
  };

  const previewFinalQuotation = () => { if (activeQuotationDoc) openDoc(activeQuotationDoc); };

  const sendFinalQuotation = () => {
    if (!activeQuotationDoc) return;
    openComposer({
      channel: "email", parties: parties.length ? parties : [contactLabel], defaultParty: parties[0] || contactLabel,
      customerId: clientId, subjectHint: `[${inquiry.id}] Quotation`, intent: `share the quotation document for ${inquiry.id}`,
      attachments: [{ id: activeQuotationDoc.id, name: activeQuotationDoc.name }],
      // Bug fix (Aug 2026): this ad-hoc send never told the backend a
      // quotation was sent, so the inquiry never showed up in the Pipeline
      // board's "Quotation" column. Record it once the Composer confirms
      // the message actually went out — see mark-quotation-sent in server.py.
      // `documentId` (Aug 2026 addition) makes each individual ad-hoc send
      // separately identifiable in `inquiry.adHocQuotationSends`, so Sales
      // can later confirm exactly which sent document the customer
      // accepted (see the "Confirm Accepted" button on each document below).
      onSent: async () => {
        try { await api.post(`/commercial/inquiries/${inquiry.id}/mark-quotation-sent`, { expectedVersion: inquiry.version, documentId: activeQuotationDoc.id }); await refetch(); }
        catch { /* non-fatal — the message itself already sent */ }
      },
    });
  };

  const openDoc = (d) => { setActiveDoc(d); setDocOpen(true); };
  const documents = docsData?.documents || [];
  const attachments = docsData?.attachments || [];
  const inquiryDocumentRows = Array.isArray(inquiryDocs) ? inquiryDocs : [];
  const vendorRows = Array.isArray(vendors) ? vendors : [];
  const finalQuotationDocs = inquiryDocumentRows.filter((d) => d.type === "Quotation" || d.type === "Customer Quotation");
  // Aug 2026 — which of these uploaded documents were actually sent to the
  // customer (append-only record on the inquiry — see mark-quotation-sent
  // in server.py), so a "Confirm Accepted" action only ever appears next to
  // a document that really went out, and each individual send stays
  // distinguishable even when the same document name was uploaded/sent
  // more than once (a revised proposal, etc).
  const adHocSends = inquiry.adHocQuotationSends || [];
  const sentAdHocDocs = finalQuotationDocs
    .map((d) => {
      const send = [...adHocSends].reverse().find((s) => s.documentId === d.id);
      return send ? { ...d, sentAt: send.sentAt } : null;
    })
    .filter(Boolean);

  return (
    <>
      {embedded ? (
        // Aug 2026 IA correction: embedded in Customer360, the client
        // identity/back-nav/contact actions already live in the page
        // header above this panel — repeating them here would be
        // clutter. This inline header only orients within the inquiry
        // itself: which one, its status, and the one inquiry-scoped
        // action (Close Opportunity) that doesn't belong in the shared
        // client-level header.
        <div className="flex flex-col items-start gap-2 px-1 pb-3 border-b border-slate-100 sm:flex-row sm:items-center sm:justify-between" data-testid="inquiry-panel-header">
          <div>
            <div className="text-sm font-semibold text-slate-800">{inquiry.id}</div>
            <div className="text-[12px] text-slate-500">{inquiry.mode || ""} {inquiry.direction || ""} · {inquiry.origin || "?"} → {inquiry.destination || "?"} · Owner {inquiry.owner || "—"}</div>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-1.5">
            {inquiry.customerInterested ? (
              <span className="text-[11px] font-medium text-emerald-700 bg-emerald-100 rounded px-2 py-1" data-testid="ws-interested-done">Interested</span>
            ) : (
              <Button size="sm" variant="outline" className="h-8" disabled={!canMarkInterested}
                title={canMarkInterested ? undefined : "Submit all required information before marking the customer interested"}
                onClick={() => setInterestedConfirmOpen(true)} data-testid="ws-mark-interested">
                Interested
              </Button>
            )}
            {canClose && (
              <Button size="sm" variant="outline" className="h-8 text-red-600 border-red-200 hover:bg-red-50" onClick={() => setCloseDialogOpen(true)} data-testid="ws-close-opportunity">
                Close Opportunity
              </Button>
            )}
          </div>
        </div>
      ) : (
        <PageHeader
          title={
            <span className="flex items-center gap-2">
              <button onClick={() => navigate(-1)} data-testid="workspace-back" className="text-slate-400 hover:text-slate-700"><ArrowLeft className="h-4 w-4" /></button>
              {client?.name || inquiry.clientId} <span className="text-slate-300 font-normal">·</span>
              <span className="text-slate-500 font-normal text-base">{inquiry.id}</span>
            </span>
          }
          subtitle={
            <span className="flex items-center gap-2 flex-wrap">
              <span>{contactLabel} · {inquiry.mode || ""} {inquiry.direction || ""} · {inquiry.origin || "?"} → {inquiry.destination || "?"} · Owner {inquiry.owner || "—"}</span>
              {clientId && (
                <button onClick={() => navigate(`/sales/customers/${clientId}`)} data-testid="ws-view-client" className="text-[12px] font-medium text-indigo-600 hover:underline">
                  View all inquiries for this client →
                </button>
              )}
            </span>
          }
          actions={
            <div className="flex items-center gap-1.5">
              <StatusPill status={inquiry.status} className="mr-1" />
              <Button size="sm" variant="outline" className="h-8" onClick={() => comms("call")} data-testid="ws-call"><Phone className="h-3.5 w-3.5 mr-1.5" />Call</Button>
              <Button size="sm" variant="outline" className="h-8" onClick={() => comms("whatsapp")} data-testid="ws-whatsapp"><MessageCircle className="h-3.5 w-3.5 mr-1.5" />WhatsApp</Button>
              <Button size="sm" variant="outline" className="h-8" onClick={() => comms("email")} data-testid="ws-email"><Mail className="h-3.5 w-3.5 mr-1.5" />Email</Button>
              {canClose && (
                <Button size="sm" variant="outline" className="h-8 text-red-600 border-red-200 hover:bg-red-50" onClick={() => setCloseDialogOpen(true)} data-testid="ws-close-opportunity">
                  Close Opportunity
                </Button>
              )}
            </div>
          }
        />
      )}
      <div className={embedded ? "" : "flex-1 overflow-y-auto"}>
        {/* Aug 2026 — this card used to duplicate Customer360's "Customer
            Conversation Summary" card (same comm.aiSummary data, two
            different titles, stacked on the same page) whenever this
            Workspace is embedded — which in production it always is; there
            is no standalone route that renders Workspace outside
            Customer360 (see the IA-correction comment at that call site).
            Only render this copy in the (test-only) standalone case. */}
        {!embedded && (
          <div className="px-6 pt-5">
            <AICard title="What the customer has told us" footer={
              <span className="text-xs font-medium text-violet-700">{comm?.intent ? `Detected intent: ${comm.intent}` : "Full conversation history is available below"}</span>
            }>
              {comm?.aiSummary || "No AI summary generated yet for this conversation."}
            </AICard>
          </div>
        )}

        {/* Sibling-inquiry switching: only rendered in the standalone
            (non-embedded) path — Customer360 already shows the full
            inquiry list right above this panel, so repeating it here
            would duplicate the same navigation surface. */}
        {!embedded && clientRecord?.inquiries?.length > 1 && (
          <div className="px-6 pt-3" data-testid="ws-sibling-inquiries">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
              Other inquiries for {clientRecord.name} · {clientRecord.inquiries.length}
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {[...clientRecord.inquiries]
                .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
                .map((sib) => (
                  <button
                    key={sib.id}
                    onClick={() => sib.id !== inquiry.id && navigate(`/sales/inquiries/${sib.id}`)}
                    data-testid={`ws-sibling-${sib.id}`}
                    className={`shrink-0 rounded-lg border px-3 py-1.5 text-left text-[12px] transition-colors ${sib.id === inquiry.id ? "border-indigo-400 bg-indigo-50 text-indigo-800" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                  >
                    <div className="font-mono-num font-semibold">{sib.id}{sib.id === inquiry.id ? " (this one)" : ""}</div>
                    <div className="text-slate-400">{sib.mode || sib.lane || displayLabel(sib.status)}</div>
                  </button>
                ))}
            </div>
          </div>
        )}

        <div className={embedded ? "grid grid-cols-1 gap-4 pt-3 2xl:grid-cols-[minmax(0,1fr)_240px]" : "grid grid-cols-1 lg:grid-cols-3 gap-5 p-6"}>
          <div className={embedded ? "min-w-0 space-y-4" : "min-w-0 space-y-4 lg:col-span-2"}>
            {isClosed ? (
              <ClosedOpportunityPanel inquiry={inquiry} onResume={resumeOpportunity} onReactivate={reactivateOpportunity} busy={busy} />
            ) : isWon ? (
              <WonOrderStatusPanel acceptance={acceptance} currentVersion={currentVersion} financeSummary={financeSummary} />
            ) : (
              <NextActionPanel nextAction={nextAction} />
            )}
            {!isClosed && !isWon && <MissingRequirementList blockers={blockers} onAskCustomer={askCustomer} onSendDocumentRequest={requestDocuments} customerId={clientId} inquiryId={inquiry.id} documents={[...documents, ...inquiryDocumentRows]} onPreview={openDoc} />}
            {/* Item 3 — Mandatory Trade Document checklist, kept as its own
                section (own header, own Card) below the general missing-
                information list above and never merged with the customer-
                level KYC checklist on Customer360. */}
            <TradeDocumentsSection inquiryId={inquiry.id} customerId={clientId} onSendDocumentRequest={requestDocuments} onPreview={openDoc} />
            <RequirementSummary inquiry={inquiry} completeness={completeness} onEdit={canEditRequirements ? openRequirements : undefined} />
            {!isClosed && !isWon && (
              <>
                {["DRAFT", "PENDING_INFORMATION"].includes(inquiry.status) && blockers.filter((b) => b.blocking).length === 0 && <WorkflowAction title="Requirements checked" description="Submit the completed requirement for feasibility review." action="Ready for feasibility" busy={busy} onClick={() => transitionInquiry("READY_FOR_FEASIBILITY")} />}
                {inquiry.status === "READY_FOR_FEASIBILITY" && <WorkflowAction title="Feasibility review" description="Start the operational feasibility assessment for this route and cargo." action="Start review" busy={busy} onClick={() => transitionInquiry("FEASIBILITY_REVIEW")} />}
                <FeasibilityCard feasibility={feasibility} inquiry={inquiry} onRun={runFeasibility} busy={busy} />
                {inquiry.status === "FEASIBILITY_REVIEW" && feasibility?.verdict === "PASS" && <WorkflowAction title="Feasibility passed" description="Release the requirement for supplier rate sourcing." action="Ready for rates" busy={busy} onClick={() => transitionInquiry("READY_FOR_RATE")} />}
                {inquiry.status === "READY_FOR_RATE" && !rateRequest && <WorkflowAction title="Supplier rate request" description="Choose approved suppliers and record the intended RFQ recipients. External delivery requires a provider integration." action="Record rate request" busy={busy} onClick={() => setRateRequestOpen(true)} />}
                <RateComparison
                  rateRequest={rateRequest} supplierRates={supplierRates} selectedSupplierRate={selectedSupplierRate}
                  seeCosts={me?.caps?.see_costs || me?.role === "sales"} onSelect={selectRate} busy={busy}
                />
                <RfqDispatchPanel rateRequest={rateRequest} readiness={rfqDispatchReadiness} onDispatch={dispatchRateRequest} busy={busy} />
                {rateRequest && !selectedSupplierRate && <WorkflowAction title="Vendor quotations" description="Record the commercial values received from a vendor quotation before comparison and selection." action="Record supplier rate" busy={busy} onClick={() => setSupplierRateOpen(true)} />}
                <Card title={`Final quotation · ${finalQuotationDocs.length}`} testId="final-quotation-upload"
                  right={<span className="text-[10px] text-slate-400">PDF or image · linked to {inquiry.id}</span>}>
                  <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-3">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-between">
                      <div className="text-[12px] text-slate-500">
                        {activeQuotationDoc ? <>Selected: <span className="font-medium text-slate-700">{activeQuotationDoc.name}</span></> : "Upload the final quotation for this customer."}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <input ref={finalQuotationConfirm.inputRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" onChange={finalQuotationConfirm.onFileChosen} data-testid="quick-share-quotation-file" />
                        {!finalQuotationConfirm.pending && (
                          <Button variant="outline" className="h-9 bg-white" disabled={shareQuotationUploading} onClick={finalQuotationConfirm.openPicker} data-testid="final-quotation-upload-btn">
                            {shareQuotationUploading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <UploadCloud className="h-4 w-4 mr-1.5" />}Upload
                          </Button>
                        )}
                        <Button variant="outline" className="h-9 bg-white" disabled={!activeQuotationDoc} onClick={previewFinalQuotation} data-testid="final-quotation-preview-btn">
                          <Eye className="h-4 w-4 mr-1.5" />Preview
                        </Button>
                        <Button className="h-9" disabled={!activeQuotationDoc} onClick={sendFinalQuotation} data-testid="final-quotation-send-btn">
                          <Send className="h-4 w-4 mr-1.5" />Send to customer
                        </Button>
                      </div>
                    </div>
                    {finalQuotationConfirm.pending && (
                      <FileSelectConfirmBar pending={finalQuotationConfirm.pending} busy={finalQuotationConfirm.busy} onSubmit={finalQuotationConfirm.submit} onCancel={finalQuotationConfirm.cancel} onReUpload={finalQuotationConfirm.reUpload} submitLabel="Submit quotation" className="mt-2" />
                    )}
                  </div>
                  {finalQuotationDocs.length > 0 && (
                    <div className="divide-y divide-slate-100 mt-2">
                      {finalQuotationDocs.map((d) => {
                        const wasSent = sentAdHocDocs.some((s) => s.id === d.id);
                        // Aug 2026 — "Confirm Accepted" only appears once
                        // this exact document has really been sent to the
                        // customer (real evidence of a send, not just an
                        // upload) — right next to its human-review status
                        // pill, per the reported gap: Sales had no way to
                        // record the customer's acceptance of a quotation
                        // sent through this ad-hoc path at all.
                        return (
                          <div key={d.id} className={`w-full py-2 flex items-center gap-3 ${activeQuotationDoc?.id === d.id ? "bg-indigo-50/60" : ""}`}>
                            <button onClick={() => { setActiveQuotationDoc(d); openDoc(d); }} className="flex-1 min-w-0 flex items-center gap-3 text-left hover:bg-slate-50 rounded-md px-1 py-0.5" data-testid={`final-quotation-doc-${d.id}`}>
                              <FileText className="h-4 w-4 text-indigo-500 shrink-0" />
                              <div className="min-w-0 flex-1"><div className="text-sm font-medium truncate">{d.name}</div><div className="text-[11px] text-slate-400">{d.date}</div></div>
                            </button>
                            <StatusPill status={d.review} />
                            {wasSent && (
                              <Button
                                size="sm" variant="outline" className="h-7 px-2 text-[11px] shrink-0 border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                                onClick={() => { setAcceptanceInitialDocId(d.id); setAcceptanceOpen(true); }}
                                data-testid={`final-quotation-confirm-accepted-${d.id}`}
                              >
                                Confirm Accepted
                              </Button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Card>
                <QuotationSummary
                  quotation={quotation} versions={versions} currentVersion={currentVersion} approval={approval}
                  onSubmitApproval={submitApproval} onSend={sendQuotation} onOpenPdf={openPdf} busy={busy}
                  canSend={!!me?.caps?.send_quotation}
                />
                {selectedSupplierRate && !currentVersion && <WorkflowAction title="Customer quotation" description="Enter customer selling charges and commercial terms using the selected supplier rate." action="Build quotation" busy={busy} onClick={openQuoteBuilder} />}
                <AcceptanceReview acceptance={acceptance} currentVersion={currentVersion} versions={versions} financeSummary={financeSummary} onVerify={verifyAcceptance} busy={busy} client={client} hideVerifyAction={embedded} documents={inquiryDocumentRows} />
                {(versions.some((v) => v.sentAt) || sentAdHocDocs.length > 0) && (!acceptance || ["REJECTED_AS_ACCEPTANCE", "INVALIDATED_BY_CHANGE", "SUPERSEDED"].includes(acceptance.status)) && (
                  <WorkflowAction
                    title="Customer response"
                    description="Review the customer's message and confirm which sent quotation (structured or ad-hoc document) they accepted."
                    action="Confirm Customer Accepted a Quotation"
                    busy={busy}
                    onClick={() => { setAcceptanceInitialDocId(null); setAcceptanceOpen(true); }}
                  />
                )}
              </>
            )}

            {/* Aug 2026 UX fix: when embedded in Customer360, that page
                already renders a "Documents" section for this same client
                (identical documents/attachments, same data source) further
                up the same screen — repeating it here duplicated the exact
                same list. Only shown in the standalone workspace path. */}
            {!embedded && (
              <Card title={`Documents · ${documents.length + attachments.length}`} testId="workspace-documents"
                right={<button onClick={() => setUpload(true)} data-testid="ws-doc-upload" className="text-[11px] font-medium text-indigo-600 hover:underline inline-flex items-center gap-1"><UploadCloud className="h-3 w-3" />Upload</button>}>
                {documents.length + attachments.length === 0 ? (
                  <div className="text-[13px] text-slate-400 py-1">No documents yet.</div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {documents.map((d) => (
                      <button key={d.id} onClick={() => openDoc(d)} data-testid={`ws-doc-${d.id}`} className="w-full text-left py-2 flex items-center gap-3 hover:bg-slate-50 transition-colors rounded-lg px-1">
                        <FileText className="h-4 w-4 text-indigo-500 shrink-0" />
                        <div className="min-w-0 flex-1"><div className="text-sm font-medium text-slate-800 truncate">{d.name}</div><div className="text-[11px] text-slate-400">{d.type} · {d.source}</div></div>
                        <StatusPill status={d.review} />
                        <ChevronRight className="h-3.5 w-3.5 text-slate-300" />
                      </button>
                    ))}
                  </div>
                )}
              </Card>
            )}
          </div>

          <div className="min-w-0 space-y-4">
            {client && (
              <Card testId="workspace-customer-card">
                <Field label="Company" value={client.name} />
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <Field label="Source" value={inquiry.leadId ? "Converted lead" : "Direct"} />
                  <Field label="Owner" value={inquiry.owner} />
                </div>
              </Card>
            )}

            {/* Aug 2026 UX fix: when embedded in Customer360, that page
                already renders the full "Communication history" for this
                client (same underlying threads) further up the same
                screen — repeating it here duplicated the exact same
                interactions. Only shown in the standalone workspace path. */}
            {!embedded && (
              <>
                <div className="flex items-center justify-between">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Interaction history</div>
                  <span className="text-[11px] text-slate-400">{comm?.threads?.length || 0} interactions</span>
                </div>
                {!comm?.threads?.length ? (
                  <EmptyState title="No interactions yet" sub="Calls, WhatsApp, email and quotation activity will appear here." />
                ) : (
                  <CommunicationTimeline threads={[...comm.threads].reverse()} documents={[...documents, ...inquiryDocumentRows]} onPreviewDocument={openDoc} />
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <AutoFileDialog open={upload} onOpenChange={(v) => { setUpload(v); if (!v) qc.invalidateQueries({ queryKey: ["cust-docs", clientId] }); }} customerId={clientId} />
      <DocumentPanel doc={activeDoc} open={docOpen} onOpenChange={setDocOpen} />
      <RateRequestDialog open={rateRequestOpen} onOpenChange={setRateRequestOpen} vendors={vendorRows} selected={rateSupplierIds} onSelected={setRateSupplierIds} onSubmit={createRateRequest} busy={busy} />
      <SupplierRateDialog open={supplierRateOpen} onOpenChange={setSupplierRateOpen} vendors={vendorRows} value={rateEntry} onChange={setRateEntry} onSubmit={addSupplierRate} busy={busy} />
      <QuotationBuilderDialog open={quoteOpen} onOpenChange={setQuoteOpen} lines={quoteLines} onLines={setQuoteLines} terms={quoteTerms} onTerms={setQuoteTerms} onSubmit={buildQuotation} busy={busy} currency={selectedSupplierRate?.currency || "AED"} />
      <ConfirmAcceptanceDialog
        open={acceptanceOpen}
        onOpenChange={(v) => { setAcceptanceOpen(v); if (!v) setAcceptanceInitialDocId(null); }}
        quotation={quotation} versions={versions}
        docCandidates={sentAdHocDocs.map((d) => ({ documentId: d.id, name: d.name, sentAt: d.sentAt }))}
        initialDocId={acceptanceInitialDocId}
        onConfirm={confirmAcceptance} onOpenPdf={openPdf}
        onOpenDoc={(d) => { const full = finalQuotationDocs.find((fd) => fd.id === d.documentId); if (full) openDoc(full); }}
        busy={busy}
      />
      <RequirementEditorDialog open={requirementsOpen} onOpenChange={changeRequirementsOpen} value={requirementsValue} onChange={setRequirementsValue} onSubmit={saveRequirements} busy={busy} />
      <CloseOpportunityDialog open={closeDialogOpen} onOpenChange={setCloseDialogOpen} inquiry={inquiry} client={client} onClosed={refetch} />
      <HandoffConfirmDialog
        open={interestedConfirmOpen}
        onOpenChange={setInterestedConfirmOpen}
        busy={busy}
        title="Mark customer interested"
        confirmLabel="Confirm"
        summaryRows={[
          { label: "Customer", value: client?.name || inquiry.clientId },
          { label: "Inquiry", value: inquiry.id },
          { label: "Route", value: `${inquiry.origin || "?"} → ${inquiry.destination || "?"}` },
        ]}
        seesHeading="What this does"
        receivingTeamSees={[
          "This is a Sales-internal flag only — it marks that Sales is actively working this customer.",
          "Nothing is sent to Finance. Finance only sees this inquiry once you explicitly click \"Send to Finance for Review\" later in the process.",
        ]}
        onConfirm={markInterested}
      />
    </>
  );
}
